import { Graphics } from 'pixi.js';
import { EngineFlare } from './EngineFlare';
import {
  ShipClass,
  SlotDefinition,
  SHIP_SPARROW,
  SHIPS,
  Engine,
  Shield,
  Weapon,
  Utility,
  Enhancer,
  getEquipmentById,
  WORLD_CONFIG,
  ENTITY_RADII,
  WARP_CONFIG,
  COLLISION_CONFIG,
  WarpPhase,
  type EntityType,
  type SelectionInfo,
  computeShieldDrag,
  SHIELD_DRAG_SPEED_THRESHOLD,
  getShipCollisionShapes,
  getShipBoundingRadius,
  type CollisionCircle,
} from '@astra/shared';
import { ShipGraphics, SHIP_DESIGNS, createSVGExplosion, createShieldShatter, ShipDebris, DEFAULT_SHIP_COLOR, SVG_SHIP_META, SHIP_ID_TO_DESIGN, SIZE_SCALE_MULTIPLIERS } from './ShipGraphics';
import { DamageableEntity } from './base/DamageableEntity';
import { degToRad, radToDeg } from '../utils/angleUtils';
import { ControlScheme, getControlScheme, getEffectiveBinding } from '../config/controls';
import { InputManager, KeyState, PointerEventData } from '../core/InputManager';
import { Camera } from '../core/Camera';
import { tween, tweens } from '../core/TweenManager';
import { emitGameEvent, onGameEvent } from '../core/GameEvents';
import { networkManager } from '../systems/NetworkManager';
import { audioManager } from '../audio/AudioManager';

// Calculated effective stats after equipment bonuses
interface EffectiveStats {
  acceleration: number;
  maxSpeed: number;
  strafeAcceleration: number;
  rotationSpeed: number;
  brakingPower: number;
  engineType: string;  // 'proton' | 'ion' | 'plasma' | ''
  shieldType: string;  // 'deflector' | 'absorber' | 'regenerator' | ''
}

// Emergency thruster multiplier when no engine is equipped
// Ship can still move forward/back, but slower (emergency thrusters only)
// Strafe requires a proper engine and is disabled without one
const EMERGENCY_THRUST_MULTIPLIER = 0.5; // 50% of base stats

export class Player extends DamageableEntity {
  // Abstract properties from SpaceEntity/TargetableEntity/CollidableEntity/DamageableEntity
  public readonly entityType: EntityType = 'localPlayer';
  public readonly selectionRadius: number = 40;
  public readonly isHostile: boolean = false; // Local player is never hostile to themselves
  public collisionRadius: number = ENTITY_RADII.PLAYER_SHIP;
  public collisionShapes: CollisionCircle[] = [];

  // Use sessionId as the id (set via setData)
  private _playerId: string = 'local';
  public get id(): string {
    return this._playerId;
  }

  // Mass is mutable based on ship class
  public mass: number;

  // Manual physics (PixiJS has no physics engine)
  // Note: velocityX/velocityY are inherited from MovableEntity
  public acceleration: { x: number; y: number } = { x: 0, y: 0 };
  public angularVelocity: number = 0;

  // Input state - dynamic key map based on custom bindings
  private keyMap: Map<string, KeyState> = new Map();
  private inputManager: InputManager | null = null;

  // Event unsubscribers (for cleanup)
  private eventUnsubscribers: Array<() => void> = [];

  // Camera reference (for mouse world position)
  private camera: Camera | null = null;

  // Control scheme
  private controlScheme: ControlScheme;
  private _isRightMouseDown: boolean = false; // Left click = fire weapon

  // Right-click drag panning
  private rightDragStart: { x: number; y: number } | null = null;
  private isRightDragging: boolean = false;
  private rightDragThreshold: number = 5; // pixels before considered a drag

  // Ship configuration
  public shipClass: ShipClass;

  // Ship graphics (procedural drawing)
  private shipGraphics: ShipGraphics;

  // Equipment slots (copy from ship class, tracks what's equipped)
  public slots: SlotDefinition[];

  // Cached effective stats (recalculated when equipment changes)
  private effectiveStats: EffectiveStats;

  // Ship state
  private isThrustingForward: boolean = false;
  private _isStrafing: boolean = false;
  public credits: number = 0;
  private _inputEnabled: boolean = true;

  // Getter/setter for inputEnabled
  public get inputEnabled(): boolean {
    return this._inputEnabled;
  }

  public set inputEnabled(value: boolean) {
    this._inputEnabled = value;
  }

  // Alert cooldown to prevent spam
  private lastAlertTime: number = 0;
  private alertCooldown: number = 2000; // 2 seconds between alerts


  // Strafe thrust flares (left and right engine flares)
  private strafeLeftFlare?: EngineFlare;
  private strafeRightFlare?: EngineFlare;

  // Reverse thrust flares (front-mounted engine flares)
  private reverseLeftFlare?: EngineFlare;
  private reverseRightFlare?: EngineFlare;
  private isReversing: boolean = false;

  // Brake speed lines (ripple effect) - use single Graphics + data array
  private brakeGraphics?: Graphics;
  private brakeLineData: Array<{ x: number; y: number; angle: number; width: number; depth: number; life: number; maxLife: number; layer: number }> = [];
  private isBraking: boolean = false;
  private _wasBrakingLastFrame: boolean = false;
  private brakeLineTimer: number = 0;

  // Shield hit effect (concave arcs around ship) - use single Graphics + data array
  private shieldGraphics?: Graphics;
  private shieldArcData: Array<{ x: number; y: number; angle: number; width: number; depth: number; life: number; maxLife: number }> = [];

  // Shield vibrate on hit (replaces arc animation)
  private _shieldVibrateTime: number = 0; // ms remaining
  private _shieldVibrateOffsetX: number = 0;
  private _shieldVibrateOffsetY: number = 0;

  // Hull damage red glow
  private _hullGlowTime: number = 0; // ms remaining

  // Shield drag visual state
  private _shieldDragActive: boolean = false;
  private _shieldDragIntensity: number = 0; // 0-1 normalized drag strength
  private shieldDragPhase: number = 0; // animation phase accumulator

  // Debris for destruction
  private debris: ShipDebris[] = [];

  // Visibility circles (shown when zoomed out far)
  private visibilityGraphics?: Graphics;

  // Damage flash
  private damageFlashTime: number = 0;

  // Collision cooldown (for camera shake effects)
  private lastCollisionTime: number = 0;
  private collisionCooldown: number = 500; // 500ms between collision effects

  // Ship color customization
  private _shipColor: number = DEFAULT_SHIP_COLOR;

  // Weapon firing
  private lastFireTime: number = 0;
  public projectiles: Graphics[] = []; // Track projectiles for cleanup

  // Ship-level heat (server-authoritative, cached from state updates)
  public shipHeat: number = 0;
  public shipMaxHeat: number = 100;
  public isShipOverheated: boolean = false;
  // Voluntary vent (hard-flux): weapons + shield-raise locked server-side;
  // mirrored here to gate local inputs and visuals so we don't desync.
  public isVenting: boolean = false;

  // Laser beam firing state (for continuous beam weapons)
  private isLaserFiring: boolean = false;

  // Selected target for missile tracking
  private selectedTarget: { id: string; type: 'bot' | 'player' | 'asteroid' | 'drone'; x?: number; y?: number } | null = null;

  // Safe zone status (no weapons/damage)
  public inSafeZone: boolean = false;

  // Boost system (energy-based, like sprinting)
  private _isBoosting: boolean = false;
  private _wasBoostingLastFrame: boolean = false;
  private boostEnergy: number = 100; // 0-100, starts full (display value, smoothly interpolated)
  private boostEnergyTarget: number = 100; // Target energy (actual logical value)
  private boostMaxEnergy: number = 100;
  private boostDrainRate: number = 33; // Energy per second while boosting (3 seconds full drain)
  private boostRechargeRate: number = 20; // Energy per second while not boosting (5 seconds full recharge)
  private boostMinToStart: number = 20; // Minimum energy needed to start boosting
  private boostSmoothSpeed: number = 8; // How fast the display catches up to target (higher = faster)

  // Radar scanning system (hold R to scan progressively farther)
  private _isScanning: boolean = false;
  private _wasScanningLastFrame: boolean = false;
  private radarHoldTime: number = 0; // Seconds holding the radar key
  private radarCurrentRange: number = 0; // Current scan range in units
  private radarLastRequestTime: number = 0; // Throttle server requests
  private radarRequestInterval: number = 500; // Request every 500ms while scanning
  private radarScanComplete: boolean = false; // True when scan reached max range
  private radarIsCharging: boolean = false; // True during cooldown
  private radarChargeRemaining: number = 0; // Seconds remaining in cooldown
  private readonly RADAR_COOLDOWN: number = 10; // 10 second cooldown after full scan
  // Radar wave animation
  private _radarWaveGraphics?: Graphics;
  private radarWaves: Array<{ radius: number; maxRadius: number; alpha: number; isBlast?: boolean }> = [];
  private radarWaveTimer: number = 0;
  private readonly RADAR_WAVE_INTERVAL: number = 300; // Spawn new wave every 300ms
  private radarBlastTriggered: boolean = false; // Track if scan complete blast was fired


  // Utility hotbar system - cooldown tracking per utility type
  private utilityCooldowns: Map<string, { until: number; total: number }> = new Map();
  // Hotbar number keys (1-5)
  private hotbarKeys: KeyState[] = [];

  // Warp Disruptor visual effect
  private _disruptorGraphics?: Graphics;
  private disruptorActiveUntil: number = 0;  // Timestamp when effect expires
  private disruptorRange: number = 0;        // Range in world units
  private disruptorWaves: Array<{ radius: number; maxRadius: number; alpha: number }> = [];
  private disruptorWaveTimer: number = 0;
  private readonly DISRUPTOR_WAVE_INTERVAL: number = 400; // Spawn wave every 400ms

  // Constellation boundary constraints (default to constellation 0,0)
  private constellationBounds: { minX: number; maxX: number; minY: number; maxY: number } = {
    minX: -WORLD_CONFIG.CONSTELLATION_SIZE / 2,
    maxX: WORLD_CONFIG.CONSTELLATION_SIZE / 2,
    minY: -WORLD_CONFIG.CONSTELLATION_SIZE / 2,
    maxY: WORLD_CONFIG.CONSTELLATION_SIZE / 2,
  };

  // Autopilot / Approach system
  private _isAutopiloting: boolean = false;
  private autopilotTarget: { x: number; y: number; entityRadius: number } | null = null;
  private autopilotStopCallback: (() => void) | null = null;

  // Warp travel system
  private _warpPhase: WarpPhase = 'none';
  private _warpTargetX: number = 0;
  private _warpTargetY: number = 0;
  private _warpSpeed: number = 0;
  private _warpCooldownUntil: number = 0;
  private _warpReadyStartTime: number = 0;  // When warp readiness detection started
  private _lastWaypointX: number = 0;       // Track waypoint for alignment
  private _lastWaypointY: number = 0;
  private _warpTrailGraphics?: Graphics;    // Warp visual effect

  // Custom data storage
  private customData: Map<string, unknown> = new Map();

  // Bounce factor for collisions
  private bounce: number = 0.3;

  // === GHOST OBJECT PATTERN: Server-Authoritative Position ===
  // Server sends authoritative position on collisions; visual position lerps toward it smoothly
  // NOTE: In ASTRA, position is client-authoritative during normal movement.
  // Server only sends corrections on collision events.
  private serverX: number = 0;
  private serverY: number = 0;
  private serverVelocityX: number = 0;
  private serverVelocityY: number = 0;
  private serverPositionInitialized: boolean = false;
  // Track when we received a server correction to apply it over time
  private hasPendingServerCorrection: boolean = false;

  // Correction blending factors (per frame at 60fps)
  // 0.15 = ~150ms to reach 90% of target
  private positionCorrectionFactor: number = 0.15;
  private velocityCorrectionFactor: number = 0.2;
  // Beyond this distance, snap instead of lerp (for respawn/warp)
  private maxSmoothCorrectionDist: number = 100;
  // Threshold below which we consider correction complete
  private correctionCompleteThreshold: number = 2;

  // === INPUT SEQUENCING: Server Reconciliation (Phase 1 networking) ===
  // Server returns lastProcessedInput so client knows which state is acknowledged
  private lastServerAckedInput: number = 0;

  // Current max velocity (changes during boost)
  private currentMaxVelocity: number;

  // Reference angle (container uses rotation in radians, but we expose angle in degrees)
  // In PixiJS, rotation 0 = pointing right. We want 0 = pointing up.
  private _angle: number = 0; // degrees, 0 = up

  public get angle(): number {
    return this._angle;
  }

  /** Distance from ship center to nose, accounting for SVG scale and ship size */
  public get noseOffset(): number {
    const designKey = SHIP_ID_TO_DESIGN[this.shipClass?.id] || this.shipClass?.id || 'corvette';
    const svgMeta = SVG_SHIP_META[designKey];
    if (svgMeta) {
      const sizeMultiplier = SIZE_SCALE_MULTIPLIERS[this.shipClass?.size || 'medium'] || 1.0;
      return svgMeta.thrustOffsetY * svgMeta.scale * sizeMultiplier;
    }
    const design = SHIP_DESIGNS[designKey] || SHIP_DESIGNS.corvette;
    return Math.abs(design.thrustOffset.y);
  }

  public set angle(value: number) {
    this._angle = value;
    // Convert to PixiJS rotation: add 90 degrees (ship drawn pointing up, PixiJS 0 = right)
    this.rotation = degToRad(value);
  }

  // Velocity getter/setter for backwards compatibility (uses inherited velocityX/velocityY)
  public get velocity(): { x: number; y: number } {
    return { x: this.velocityX, y: this.velocityY };
  }

  public set velocity(v: { x: number; y: number }) {
    this.velocityX = v.x;
    this.velocityY = v.y;
    this.targetVelocityX = v.x;
    this.targetVelocityY = v.y;
  }

  /**
   * Stop all movement immediately.
   * Extends base class to also zero acceleration and angular velocity.
   * Use for docking, mining, or any mechanic that needs to halt the ship.
   */
  override stop(): void {
    super.stop();
    this.acceleration.x = 0;
    this.acceleration.y = 0;
    this.angularVelocity = 0;
  }

  constructor(x: number, y: number, shipClass: ShipClass = SHIP_SPARROW, shipColor?: number) {
    super();

    this.position.set(x, y);
    // Initialize target position for interpolation
    this.targetX = x;
    this.targetY = y;

    this.shipClass = shipClass;
    this.collisionShapes = getShipCollisionShapes(shipClass.class, shipClass.size);
    this.collisionRadius = getShipBoundingRadius(shipClass.class, shipClass.size);
    this.stunIndicatorRadius = this.noseOffset + 10;
    this.controlScheme = getControlScheme();
    this.hull = shipClass.stats.hull;
    this.maxHull = shipClass.stats.hull;
    // Shield starts at 50% of hull (can be modified by equipment later)
    this.maxShield = Math.floor(shipClass.stats.hull * 0.5);
    this.shield = this.maxShield;
    this.mass = shipClass.stats.mass;

    // Set ship color if provided
    if (shipColor !== undefined) {
      this._shipColor = shipColor;
    }

    // Deep copy slots so we can modify equipment
    this.slots = shipClass.slots.map((slot: SlotDefinition) => ({ ...slot }));

    // Calculate initial effective stats based on equipped items
    this.effectiveStats = this.calculateEffectiveStats();
    this.currentMaxVelocity = this.effectiveStats.maxSpeed;

    // Create procedural ship graphics with color and size
    this.shipGraphics = new ShipGraphics(0, 0, shipClass.id, {
      color: this._shipColor,
      size: shipClass.size,
      tier: shipClass.tier,
    });
    this.addChild(this.shipGraphics);

    // Set up engine trail
    this.setupTrail();
  }

  // Initialize with InputManager and Camera (called from scene)
  public init(inputManager: InputManager, camera: Camera): void {
    this.inputManager = inputManager;
    this.camera = camera;

    // Generate thrust textures now that renderer is available
    this.shipGraphics.generateThrustTextures();

    // Set up controls
    this.setupControls();
  }

  // Calculate effective stats from base ship stats + equipment bonuses
  private calculateEffectiveStats(): EffectiveStats {
    const baseStats = this.shipClass.stats;

    // Start with base stats
    let acceleration = baseStats.baseAcceleration;
    let maxSpeed = baseStats.baseMaxSpeed;
    let strafeAcceleration = baseStats.baseStrafeAcceleration;

    // Multipliers (applied after bonuses)
    let accelMultiplier = 1.0;
    let speedMultiplier = 1.0;
    let strafeMultiplier = 1.0;

    // Track if we have an engine equipped
    let hasEngineEquipped = false;
    let engineType = '';
    let shieldType = '';

    // Process equipped items
    for (const slot of this.slots) {
      if (!slot.equipped) continue;

      const equipment = getEquipmentById(slot.equipped);
      if (!equipment) continue;

      // Engine bonuses - single engine controls all movement (forward, back, strafe)
      if (equipment.category === 'ENGINE') {
        hasEngineEquipped = true;
        const engine = equipment as Engine;
        engineType = engine.engineType;

        // Engine provides both main propulsion AND strafe capability
        acceleration += engine.stats.accelerationBonus;
        maxSpeed += engine.stats.maxSpeedBonus;
        accelMultiplier *= engine.stats.accelerationMultiplier;
        speedMultiplier *= engine.stats.maxSpeedMultiplier;

        // Strafe capability is now built into the engine
        strafeAcceleration += engine.stats.strafeBonus;
        strafeMultiplier *= engine.stats.strafeMultiplier;
      }

      if (equipment.category === 'SHIELD') {
        shieldType = (equipment as Shield).shieldType;
      }
    }

    // Apply multipliers
    acceleration = Math.floor(acceleration * accelMultiplier);
    maxSpeed = Math.floor(maxSpeed * speedMultiplier);
    strafeAcceleration = Math.floor(strafeAcceleration * strafeMultiplier);

    // Combat-overhaul: movement is native — no engine penalty. Ships always
    // move at their base stats. Thruster Booster enhancer extends the boost
    // (handled elsewhere) but is not required for forward/strafe motion.
    void hasEngineEquipped; // intentionally unused after overhaul
    void engineType;
    void shieldType;

    return {
      acceleration,
      maxSpeed,
      strafeAcceleration,
      rotationSpeed: baseStats.rotationSpeed,
      brakingPower: baseStats.brakingPower,
      engineType,
      shieldType,
    };
  }

  // Recalculate stats when equipment changes
  public recalculateStats(): void {
    this.effectiveStats = this.calculateEffectiveStats();
    this.currentMaxVelocity = this.effectiveStats.maxSpeed;
    this.initAmmoState();
  }

  // Legacy ammo init — no-op under combat overhaul (heat-only, no magazines)
  private initAmmoState(): void { /* no-op */ }

  // Get current effective stats (for UI display)
  public getEffectiveStats(): EffectiveStats {
    return { ...this.effectiveStats };
  }

  // Shield drag state for HUD
  public isShieldDragActive(): boolean { return this._shieldDragActive; }
  public getShieldDragIntensity(): number { return this._shieldDragIntensity; }

  // Calculate AIP — sum of equipped items' power cost
  // Used for target inspection and new player protection display
  public getTotalPower(): number {
    let totalPower = 0;

    for (const slot of this.slots) {
      if (!slot.equipped) continue;
      const equipment = getEquipmentById(slot.equipped);
      if (equipment) {
        totalPower += equipment.power || 0;
      }
    }

    return totalPower;
  }

  // Change ship class (e.g., when loaded from server or switching ships)
  public setShipClass(shipClassId: string): void {
    const newShipClass = SHIPS[shipClassId];
    if (!newShipClass) {
      console.warn(`Unknown ship class: ${shipClassId}, keeping current ship`);
      return;
    }

    // Skip if already the same ship
    if (this.shipClass.id === shipClassId) {
      return;
    }

    // Update ship class
    this.shipClass = newShipClass;

    // Update collision shapes and bounding radius
    this.collisionShapes = getShipCollisionShapes(newShipClass.class, newShipClass.size);
    this.collisionRadius = getShipBoundingRadius(newShipClass.class, newShipClass.size);
    this.stunIndicatorRadius = this.noseOffset + 10;

    // Update mass
    this.mass = newShipClass.stats.mass;

    // Update hull/shield stats (keep current percentage if damaged)
    const hullPercent = this.hull / this.maxHull;
    const shieldPercent = this.shield / this.maxShield;
    this.maxHull = newShipClass.stats.hull;
    this.hull = Math.floor(this.maxHull * hullPercent);
    this.maxShield = Math.floor(newShipClass.stats.hull * 0.5);
    this.shield = Math.floor(this.maxShield * shieldPercent);

    // Deep copy slots (keeps existing equipment references)
    // Note: this may need adjustment if slot layout changes
    this.slots = newShipClass.slots.map((slot: SlotDefinition) => ({ ...slot }));

    // Recalculate effective stats
    this.effectiveStats = this.calculateEffectiveStats();
    this.currentMaxVelocity = this.effectiveStats.maxSpeed;

    // Remove old ship graphics
    this.removeChild(this.shipGraphics);
    this.shipGraphics.destroy();

    // Create new ship graphics with correct design and size
    this.shipGraphics = new ShipGraphics(0, 0, shipClassId, {
      color: this._shipColor,
      size: newShipClass.size,
      tier: newShipClass.tier,
    });
    this.addChild(this.shipGraphics);

    // Re-setup trail for new graphics
    this.setupTrail();

    // Generate thrust textures if renderer is available
    if (this.inputManager) {
      this.shipGraphics.generateThrustTextures();
    }
  }

  private setupControls(): void {
    if (!this.inputManager) return;

    // Build key map from effective bindings
    this.rebuildKeyMap();

    // Mouse input for keys_mouse control scheme
    // Left click = fire (only on game canvas, not UI elements)
    this.inputManager.onPointerDown((pointer: PointerEventData) => {
      if (pointer.leftButtonDown && pointer.isOnCanvas) {
        this._isRightMouseDown = true;
      }
      if (pointer.rightButtonDown && this.camera) {
        // Start tracking potential right-click drag
        this.rightDragStart = { x: pointer.x, y: pointer.y };
        this.isRightDragging = false;
      }
    });

    this.inputManager.onPointerMove((pointer: PointerEventData) => {
      // Handle right-click drag for camera panning
      if (this.rightDragStart && this.camera) {
        const dx = pointer.x - this.rightDragStart.x;
        const dy = pointer.y - this.rightDragStart.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance > this.rightDragThreshold) {
          // This is a drag, not a click
          if (!this.isRightDragging) {
            this.isRightDragging = true;
            this.camera.startPan();
          }
          // Update pan offset (drag left to see right - like moving a rug under the ship)
          this.camera.setPanOffset(dx, dy);
        }
      }
    });

    this.inputManager.onPointerUp((pointer: PointerEventData) => {
      if (!pointer.leftButtonDown) {
        this._isRightMouseDown = false;
      }

      // Handle left-click release - stop laser fire
      if (pointer.button === 0) {
        this.stopLaserFire();
      }

      // Handle right-click release
      if (pointer.button === 2 && this.camera) {
        if (this.isRightDragging) {
          // Was dragging - stop panning (camera will snap back)
          this.camera.stopPan();
        } else if (this.rightDragStart) {
          // Was a click (not a drag) - emit selection event
          const worldX = this.camera.getWorldX(pointer.x);
          const worldY = this.camera.getWorldY(pointer.y);
          emitGameEvent('selectionClick', {
            worldX,
            worldY,
            screenX: pointer.x,
            screenY: pointer.y,
          });
        }
        // Reset drag state
        this.rightDragStart = null;
        this.isRightDragging = false;
      }
    });

    // Listen for binding changes via global event system
    this.eventUnsubscribers.push(
      onGameEvent('bindingsChanged', this.rebuildKeyMap.bind(this))
    );

    // Anti-cheat (Layer A): server rejected our last input as physically
    // impossible. Snap to its authoritative position so local prediction
    // doesn't drift further. The legit-player case is never reached because
    // the server's TOLERANCE covers normal jitter; reaching this means either
    // a real exploit attempt or a pathological network event — snap is safe.
    this.eventUnsubscribers.push(
      onGameEvent('positionCorrection', (data) => {
        if (!data) return;
        this.x = data.x;
        this.y = data.y;
        this.rotation = data.rotation;
        this.velocityX = data.velocityX;
        this.velocityY = data.velocityY;
      })
    );

  }

  // Rebuild key map when bindings change
  private rebuildKeyMap(): void {
    if (!this.inputManager) return;

    // Clear existing keys
    this.keyMap.clear();

    // Get effective bindings and create keys. fastTravel + follow added 2026-05
    // (handleFastTravelKey / handleFollowKey use isActionJustPressed on them);
    // without the registration the keys are silently ignored.
    const actions = ['accelerate', 'reverse', 'brake', 'strafeLeft', 'strafeRight', 'rotateLeft', 'rotateRight', 'primaryAction', 'secondaryAction', 'boost', 'radar', 'fastTravel', 'follow'] as const;

    for (const action of actions) {
      const binding = getEffectiveBinding(action, this.controlScheme);

      // Skip mouse bindings
      if (binding === 'mouse' || binding === 'mouse_left' || binding === 'mouse_right') continue;

      const key = this.inputManager.addKey(binding);
      this.keyMap.set(action, key);
    }

    // Register hotbar number keys (1-5) for utility activation
    this.hotbarKeys = [];
    for (let i = 1; i <= 5; i++) {
      this.hotbarKeys.push(this.inputManager.addKey(`${i}`));
    }
  }

  // Helper to check if an action key is pressed
  private isActionPressed(action: string): boolean {
    const key = this.keyMap.get(action);
    const isDown = key ? key.isDown : false;
    return isDown;
  }

  // Helper to check if an action key was just pressed this frame
  private isActionJustPressed(action: string): boolean {
    const key = this.keyMap.get(action);
    return key ? key.justDown : false;
  }

  // Set control scheme (called from settings)
  public setControlScheme(scheme: ControlScheme): void {
    this.controlScheme = scheme;
    this.rebuildKeyMap(); // Rebuild keys for new scheme
  }

  // Get current control scheme
  public getControlScheme(): ControlScheme {
    return this.controlScheme;
  }

  // Check if secondary action (right mouse) is pressed - for future use (mining, etc.)
  public isSecondaryActionPressed(): boolean {
    return this._isRightMouseDown;
  }

  private setupTrail(): void {
    // Destroy old child flares if they exist (ship-local effects that change with ship class)
    this.strafeLeftFlare?.destroy();
    this.strafeRightFlare?.destroy();
    this.reverseLeftFlare?.destroy();
    this.reverseRightFlare?.destroy();

    const shipClassId = this.shipClass?.id || 'corvette';
    const designKey = SHIP_ID_TO_DESIGN[shipClassId] || shipClassId;
    const design = SHIP_DESIGNS[designKey] || SHIP_DESIGNS.corvette;
    const sizeMultiplier = SIZE_SCALE_MULTIPLIERS[this.shipClass?.size || 'medium'] || 1.0;
    const svgMeta = SVG_SHIP_META[designKey];

    // Calculate ship half-width and nose Y, scaled by ship size
    let shipHalfWidth: number;
    let noseY: number;
    if (svgMeta) {
      const totalScale = svgMeta.scale * sizeMultiplier;
      // SVG rotated -90°: SVG height becomes horizontal ship width
      // Hull fills ~95% of viewBox; place thrusters at ~85% of half-width (inside hull edge)
      shipHalfWidth = (svgMeta.height / 2) * totalScale * 0.85;
      noseY = -svgMeta.thrustOffsetY * totalScale;
    } else {
      const xCoords = design.points.map((p: { x: number }) => p.x);
      shipHalfWidth = ((Math.max(...xCoords) - Math.min(...xCoords)) * design.scale * sizeMultiplier) / 2;
      noseY = Math.min(...design.points.map((p: { y: number }) => p.y)) * design.scale * sizeMultiplier;
    }

    // Create strafe flares (rotated 90deg, positioned on ship sides)
    // Scale flare size with ship size multiplier
    this.strafeLeftFlare = new EngineFlare(designKey, 0.4 * sizeMultiplier);
    this.strafeLeftFlare.rotation = Math.PI / 2; // flame points left
    this.strafeLeftFlare.position.set(-shipHalfWidth, 0);
    this.strafeLeftFlare.generateTextures();
    this.addChild(this.strafeLeftFlare);

    this.strafeRightFlare = new EngineFlare(designKey, 0.4 * sizeMultiplier);
    this.strafeRightFlare.rotation = -Math.PI / 2; // flame points right
    this.strafeRightFlare.position.set(shipHalfWidth, 0);
    this.strafeRightFlare.generateTextures();
    this.addChild(this.strafeRightFlare);

    // Create reverse flares (rotated 180deg, positioned near nose on left/right sides)
    const sideOffset = shipHalfWidth * 0.7;
    const flameY = noseY + 8;

    this.reverseLeftFlare = new EngineFlare(designKey, 0.35 * sizeMultiplier);
    this.reverseLeftFlare.rotation = Math.PI; // flame points forward (up)
    this.reverseLeftFlare.position.set(-sideOffset, flameY);
    this.reverseLeftFlare.generateTextures();
    this.addChild(this.reverseLeftFlare);

    this.reverseRightFlare = new EngineFlare(designKey, 0.35 * sizeMultiplier);
    this.reverseRightFlare.rotation = Math.PI; // flame points forward (up)
    this.reverseRightFlare.position.set(sideOffset, flameY);
    this.reverseRightFlare.generateTextures();
    this.addChild(this.reverseRightFlare);

    // World-space graphics (brake, shield, visibility, warp trail, radar waves)
    // Only create once — these are added to worldContainer by the scene via getWorldSpaceGraphics().
    // Recreating them here would orphan the old ones in worldContainer.
    if (!this.brakeGraphics) {
      this.brakeGraphics = new Graphics();
      this.brakeGraphics.zIndex = 85;

      this.shieldGraphics = new Graphics();
      this.shieldGraphics.zIndex = 15;

      this.visibilityGraphics = new Graphics();
      this.visibilityGraphics.zIndex = 80;

      this._warpTrailGraphics = new Graphics();
      this._warpTrailGraphics.zIndex = 83;

      this._radarWaveGraphics = new Graphics();
      this._radarWaveGraphics.zIndex = 82;

      this._disruptorGraphics = new Graphics();
      this._disruptorGraphics.zIndex = 81;

      // Combat-overhaul: dedicated layer for natural shield field + overload ring
      this._overhaulVfxGraphics = new Graphics();
      this._overhaulVfxGraphics.zIndex = 18;

    }
  }

  private _overhaulVfxGraphics?: Graphics;
  private _overloadPhase: number = 0; // animation phase for overload ring

  // Venting effect particles (hot air radiating outward from ship)
  private _ventParticles: Array<{
    angle: number;      // radial angle from ship center
    dist: number;       // current distance from ship center
    speed: number;      // outward speed (units per frame tick)
    size: number;       // particle radius
    alpha: number;      // current alpha
    drift: number;      // angular drift per tick (slight swirl)
    life: number;       // 0..1, increases over lifetime
    maxLife: number;    // total lifetime in normalized units
  }> = [];
  private _ventTime: number = 0;

  // Get world-space graphics for parent to manage
  public getWorldSpaceGraphics(): { brake: Graphics; shield: Graphics; visibility: Graphics; warpTrail: Graphics; radarWaves: Graphics; disruptor: Graphics; overhaulVfx: Graphics } {
    return {
      brake: this.brakeGraphics!,
      shield: this.shieldGraphics!,
      visibility: this.visibilityGraphics!,
      warpTrail: this._warpTrailGraphics!,
      radarWaves: this._radarWaveGraphics!,
      disruptor: this._disruptorGraphics!,
      overhaulVfx: this._overhaulVfxGraphics!,
    };
  }

  update(delta: number): void {
    // Update stun state from base class
    this.updateStunState();

    if (this.isDestroyed) {
      // Update debris
      this.debris = this.debris.filter(d => d.update(delta));
      return;
    }

    // Handle warp traveling separately (skip normal movement)
    if (this._warpPhase === 'traveling') {
      this.handleWarpTraveling(delta);
      this.updateWarpTrailEffect();
      this.updateVisibilityCircles();
      this.shipGraphics.update();
      return;
    }

    // Check if warp cooldown has expired and transition to 'none'
    if (this._warpPhase === 'cooldown' && Date.now() >= this._warpCooldownUntil) {
      this._warpPhase = 'none';
      this._warpCooldownUntil = 0;
      emitGameEvent('warpStateChanged', {
        phase: 'none',
        targetX: 0,
        targetY: 0,
        cooldownUntil: 0,
      });
    }

    // Check for warp readiness (when waypoint is set)
    if (this._lastWaypointX !== 0 || this._lastWaypointY !== 0) {
      if (this._warpPhase === 'none') {
        this.checkWarpReadiness();
      }
    }

    this.handleMovement(delta);
    this.applyPhysics(delta);
    this.applyServerCorrection_Lerp(); // Ghost pattern: smooth lerp toward server position
    this.enforceConstellationBounds();
    this.handleWeaponFiring();
    this.handleFastTravelKey();
    this.handleFollowKey();
    this.handleRadarScan(delta);
    this.updateRadarWaves(delta);
    this.updateDisruptorEffect(delta);
    this.handleHotbarInput();
    this.updateStrafeThrust();
    this.updateReverseThrust();
    this.updateBrakeSparks(delta);
    this.updateShieldHitArcs(delta);
    this.updateShieldDragVisual(delta);
    this.updateNaturalShieldVisual(delta);
    this.updateOverloadVisual(delta);
    this.updateVentingEffect(delta);
    this.updateVisibilityCircles();
    this.updateWarpTrailEffect();
    this.shipGraphics.update();

    // Update damage flash
    if (this.damageFlashTime > 0) {
      this.damageFlashTime -= delta;
      if (this.damageFlashTime <= 0) {
        this.shipGraphics.setAlpha(1);
      }
    }

    // Update hull damage red glow
    if (this._hullGlowTime > 0) {
      this._hullGlowTime -= delta;
      if (this._hullGlowTime <= 0) {
        this.shipGraphics.setDamageGlow(false);
      }
    }
  }

  // Manual physics update (PixiJS has no physics engine)
  private applyPhysics(delta: number): void {
    const dt = delta / 1000; // Convert to seconds

    // When stunned, only apply velocity (no acceleration or speed clamping)
    // This allows the player to drift with the bounce momentum from collision
    if (this.isStunned) {
      // Apply velocity to position (drift continues during stun)
      this.x += this.velocityX * dt;
      this.y += this.velocityY * dt;

      // Don't apply acceleration, rotation, or speed limits while stunned
      return;
    }

    // Apply acceleration to velocity
    this.velocityX += this.acceleration.x * dt;
    this.velocityY += this.acceleration.y * dt;

    // Clamp to max velocity
    const speed = Math.sqrt(this.velocityX ** 2 + this.velocityY ** 2);
    if (speed > this.currentMaxVelocity) {
      const scale = this.currentMaxVelocity / speed;
      this.velocityX *= scale;
      this.velocityY *= scale;
    }

    // Apply velocity to position
    this.x += this.velocityX * dt;
    this.y += this.velocityY * dt;

    // Apply angular velocity to rotation
    this._angle += this.angularVelocity * dt;
    this.rotation = degToRad(this._angle);
  }

  /**
   * GHOST OBJECT PATTERN: Smooth lerp toward server-authoritative position.
   * Called each frame after applyPhysics().
   *
   * This ensures collision corrections from the server are applied smoothly
   * instead of causing jarring position snaps.
   *
   * NOTE: Only applies when there's a pending server correction (collision event).
   * During normal movement, position is client-authoritative.
   */
  private applyServerCorrection_Lerp(): void {
    // Skip if no pending correction (normal movement is client-authoritative)
    if (!this.hasPendingServerCorrection) return;

    // Calculate distance to server position
    const dx = this.serverX - this.x;
    const dy = this.serverY - this.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    // If distance is large (respawn, warp, teleport), snap immediately
    if (dist > this.maxSmoothCorrectionDist) {
      this.x = this.serverX;
      this.y = this.serverY;
      this.velocityX = this.serverVelocityX;
      this.velocityY = this.serverVelocityY;
      this.hasPendingServerCorrection = false;
      return;
    }

    // Small correction: smooth lerp toward server position
    if (dist > this.correctionCompleteThreshold) {
      this.x += dx * this.positionCorrectionFactor;
      this.y += dy * this.positionCorrectionFactor;

      // Blend velocity toward server velocity
      const dvx = this.serverVelocityX - this.velocityX;
      const dvy = this.serverVelocityY - this.velocityY;
      this.velocityX += dvx * this.velocityCorrectionFactor;
      this.velocityY += dvy * this.velocityCorrectionFactor;
    } else {
      // Correction complete - snap to final position and clear flag
      this.x = this.serverX;
      this.y = this.serverY;
      this.velocityX = this.serverVelocityX;
      this.velocityY = this.serverVelocityY;
      this.hasPendingServerCorrection = false;
    }
  }

  /**
   * Set the constellation bounds for boundary collision. `constellationSize` is the FULL
   * size in world units (mirrors WORLD_CONFIG.CONSTELLATION_SIZE / GameState.constellationSize).
   * Defaults to the global constant when called before state arrives.
   */
  setConstellationBounds(
    _constellationX: number,
    _constellationY: number,
    constellationSize: number = WORLD_CONFIG.CONSTELLATION_SIZE,
  ): void {
    // Constellation bounds — LOCAL coordinates, constellation is centered at (0,0).
    const halfSize = constellationSize / 2;
    this.constellationBounds = {
      minX: -halfSize,
      maxX: halfSize,
      minY: -halfSize,
      maxY: halfSize,
    };
  }

  /**
   * Clamp player position to constellation bounds and zero velocity if hitting wall.
   * Uses ship radius offset so the ship's EDGE touches the wall, not its center.
   */
  private enforceConstellationBounds(): void {
    const bounds = this.constellationBounds;
    const shipRadius = ENTITY_RADII.PLAYER_SHIP;

    // Effective bounds account for ship radius
    const effectiveMinX = bounds.minX + shipRadius;
    const effectiveMaxX = bounds.maxX - shipRadius;
    const effectiveMinY = bounds.minY + shipRadius;
    const effectiveMaxY = bounds.maxY - shipRadius;

    let hitWall = false;

    // Clamp X position
    if (this.x < effectiveMinX) {
      this.x = effectiveMinX;
      this.velocityX = Math.max(0, this.velocityX); // Only allow moving away from wall
      hitWall = true;
    } else if (this.x > effectiveMaxX) {
      this.x = effectiveMaxX;
      this.velocityX = Math.min(0, this.velocityX);
      hitWall = true;
    }

    // Clamp Y position
    if (this.y < effectiveMinY) {
      this.y = effectiveMinY;
      this.velocityY = Math.max(0, this.velocityY);
      hitWall = true;
    } else if (this.y > effectiveMaxY) {
      this.y = effectiveMaxY;
      this.velocityY = Math.min(0, this.velocityY);
      hitWall = true;
    }

    // If we hit a wall, also zero out acceleration pushing into it
    if (hitWall) {
      const accelX = this.acceleration.x;
      const accelY = this.acceleration.y;

      if (this.x <= effectiveMinX && accelX < 0) {
        this.acceleration.x = 0;
      } else if (this.x >= effectiveMaxX && accelX > 0) {
        this.acceleration.x = 0;
      }

      if (this.y <= effectiveMinY && accelY < 0) {
        this.acceleration.y = 0;
      } else if (this.y >= effectiveMaxY && accelY > 0) {
        this.acceleration.y = 0;
      }
    }
  }

  private handleWeaponFiring(): void {
    // Skip if input disabled or stunned
    if (!this.inputEnabled || !this.canAcceptInput()) return;

    // Right click = fire weapon
    if (this._isRightMouseDown) {
      this.fireWeapon();
    }
  }

  // Combat-overhaul: movement is native to the ship. Engine slots/items were
  // removed, so every ship "has an engine" by default. The Thruster Booster
  // enhancer amplifies the native boost but is not required for movement.
  private hasEngine(): boolean {
    return true;
  }

  // Get equipped weapon
  // Active weapon slot index (-1 = pick first). Cycled by Tab key.
  private _activeWeaponSlotIndex: number = -1;

  private getEquippedWeapon(): Weapon | null {
    // Prefer the active slot if set and still has a weapon equipped
    if (this._activeWeaponSlotIndex >= 0) {
      const active = this.slots[this._activeWeaponSlotIndex];
      if (active && active.type === 'WEAPON' && active.equipped) {
        const eq = getEquipmentById(active.equipped);
        if (eq && eq.category === 'WEAPON') return eq as Weapon;
      }
    }
    const weaponSlot = this.slots.find(
      slot => slot.type === 'WEAPON' && slot.equipped
    );
    if (!weaponSlot || !weaponSlot.equipped) return null;

    const equipment = getEquipmentById(weaponSlot.equipped);
    if (equipment && equipment.category === 'WEAPON') {
      return equipment as Weapon;
    }
    return null;
  }

  /** Cycle to the next equipped weapon slot. Returns true if a switch happened. */
  public cycleWeapon(): boolean {
    const weaponSlotIndices: number[] = [];
    this.slots.forEach((s, i) => {
      if (s.type === 'WEAPON' && s.equipped) weaponSlotIndices.push(i);
    });
    if (weaponSlotIndices.length <= 1) return false;
    let curPos = weaponSlotIndices.indexOf(this._activeWeaponSlotIndex);
    if (curPos === -1) curPos = 0;
    const nextPos = (curPos + 1) % weaponSlotIndices.length;
    this._activeWeaponSlotIndex = weaponSlotIndices[nextPos];
    // Cancel laser fire if switching away from it
    this.stopLaserFire();
    try { networkManager.sendSetActiveWeapon(this._activeWeaponSlotIndex); } catch { /* offline */ }
    return true;
  }

  public getActiveWeaponSlotIndex(): number {
    return this._activeWeaponSlotIndex;
  }

  /** Return info about the active weapon for HUD display. */
  public getActiveWeaponInfo(): { name: string; weaponType: string; index: number; total: number } | null {
    const weapon = this.getEquippedWeapon();
    if (!weapon) return null;
    const equipped = this.slots.filter(s => s.type === 'WEAPON' && s.equipped);
    const activeIdx = equipped.findIndex((s, i) => {
      const slotIdx = this.slots.indexOf(s);
      return slotIdx === this._activeWeaponSlotIndex || (this._activeWeaponSlotIndex < 0 && i === 0);
    });
    return {
      name: weapon.name,
      weaponType: weapon.weaponType,
      index: Math.max(0, activeIdx),
      total: equipped.length,
    };
  }

  // Check if a weapon is equipped
  public hasWeapon(): boolean {
    return this.getEquippedWeapon() !== null;
  }

  // Get equipped booster (enhancer with booster type)
  private getEquippedBooster(): Enhancer | null {
    const boosterSlot = this.slots.find(slot => {
      if (slot.type !== 'ENHANCER' || !slot.equipped) return false;
      const equipment = getEquipmentById(slot.equipped);
      if (!equipment || equipment.category !== 'ENHANCER') return false;
      return (equipment as Enhancer).enhancerType === 'booster';
    });

    if (!boosterSlot || !boosterSlot.equipped) return null;

    const equipment = getEquipmentById(boosterSlot.equipped);
    if (equipment && equipment.category === 'ENHANCER') {
      return equipment as Enhancer;
    }
    return null;
  }

  // Get equipped sensor/radar (enhancer with sensor_array type)
  private getEquippedSensor(): Enhancer | null {
    const sensorSlot = this.slots.find(slot => {
      if (slot.type !== 'ENHANCER' || !slot.equipped) return false;
      const equipment = getEquipmentById(slot.equipped);
      if (!equipment || equipment.category !== 'ENHANCER') return false;
      return (equipment as Enhancer).enhancerType === 'sensor_array';
    });

    if (!sensorSlot || !sensorSlot.equipped) return null;

    const equipment = getEquipmentById(sensorSlot.equipped);
    if (equipment && equipment.category === 'ENHANCER') {
      return equipment as Enhancer;
    }
    return null;
  }

  // Public accessor for GameScene inspect range check
  public getEquippedSensorPublic(): Enhancer | null {
    return this.getEquippedSensor();
  }

  // Combat-overhaul: radar is native — always available.
  public hasRadar(): boolean {
    return true;
  }

  // Get sensorBonus from equipped sensor array (adds to base sensorRange for overview detection)
  public getSensorBonus(): number {
    const sensor = this.getEquippedSensor();
    return sensor?.stats.sensorBonus || 0;
  }

  // Get current radar scanning state
  public isScanning(): boolean {
    return this._isScanning;
  }

  // Get current radar scan range
  public getRadarRange(): number {
    return this.radarCurrentRange;
  }

  // Calculate scan range based on hold time and radar tier
  private calculateScanRange(holdTime: number, times: number[], ranges: number[]): number {
    // Linear interpolation between time/range breakpoints
    if (holdTime <= 0) return 0;
    if (holdTime >= times[times.length - 1]) return ranges[ranges.length - 1];

    for (let i = 0; i < times.length; i++) {
      if (holdTime <= times[i]) {
        const prevTime = i === 0 ? 0 : times[i - 1];
        const prevRange = i === 0 ? 0 : ranges[i - 1];
        const progress = (holdTime - prevTime) / (times[i] - prevTime);
        return prevRange + progress * (ranges[i] - prevRange);
      }
    }
    return ranges[ranges.length - 1];
  }

  // Native radar defaults (weaker than T1 Sensor Array).
  // Combat-overhaul: scan is NATIVE to every ship. Sensor Array enhancer
  // amplifies it but is not required — same pattern as boost.
  private static readonly NATIVE_RADAR_TIMES  = [0.8, 2.0, 4.0];
  private static readonly NATIVE_RADAR_RANGES = [5625, 11250, 16875];


  // F key: toggle approach autopilot. Press to start (uses selected entity or
  // current waypoint as target — GameScene resolves that); press again to stop.
  // The binding key is `fastTravel` in controls.ts — labeled "Approach" in the
  // UI. Warp is a separate mechanic and is engaged via its own UI button.
  private handleFastTravelKey(): void {
    if (!this.isActionJustPressed('fastTravel')) return;
    if (this._isAutopiloting) {
      // Cancel — GameScene will clear its approachTarget via the stop event.
      emitGameEvent('stopFastTravel', undefined);
      return;
    }
    // Ask GameScene to start. It picks waypoint > selected entity as target.
    emitGameEvent('startFastTravel', undefined);
  }

  // X key: toggle follow on the currently selected entity. GameScene owns the
  // selection state, so we just emit a void event and let it resolve the target.
  // Follow ≠ Approach: follow continuously tracks a moving target; approach
  // is a one-shot autopilot to a fixed point.
  private handleFollowKey(): void {
    if (!this.isActionJustPressed('follow')) return;
    emitGameEvent('toggleFollow', undefined);
  }

  // Handle radar scanning when R key is held
  private handleRadarScan(delta: number): void {
    const deltaSeconds = delta / 1000;
    const radarKeyPressed = this.isActionPressed('radar');
    const sensor = this.getEquippedSensor();

    // Combat-overhaul: scan is native. Use sensor stats if equipped,
    // otherwise fall back to weaker native defaults.
    const times  = sensor?.stats.radarScanTimes  ?? Player.NATIVE_RADAR_TIMES;
    const ranges = sensor?.stats.radarScanRanges ?? Player.NATIVE_RADAR_RANGES;
    const maxRange = ranges[ranges.length - 1];
    const maxTime = times[times.length - 1];

    // Handle charging cooldown
    if (this.radarIsCharging) {
      this.radarChargeRemaining -= deltaSeconds;
      if (this.radarChargeRemaining <= 0) {
        this.radarIsCharging = false;
        this.radarChargeRemaining = 0;
        this.radarScanComplete = false;
      } else {
        emitGameEvent('radarScanUpdate', {
          isScanning: false,
          hasRadar: true,
          currentRange: 0,
          maxRange,
          scanProgress: 0,
          holdTime: 0,
          scanComplete: false,
          isCharging: true,
          chargeRemaining: Math.ceil(this.radarChargeRemaining),
        });
        return;
      }
    }

    if (radarKeyPressed) {
      // Start or continue radar sweep (no target, or still holding)
      this._isScanning = true;
      this.radarHoldTime += deltaSeconds;

      const wasComplete = this.radarScanComplete;
      if (this.radarHoldTime >= maxTime) {
        this.radarHoldTime = maxTime;
        this.radarScanComplete = true;
      }

      this.radarCurrentRange = this.calculateScanRange(this.radarHoldTime, times, ranges);
      const scanProgress = this.radarHoldTime / maxTime;

      emitGameEvent('radarScanUpdate', {
        isScanning: true,
        hasRadar: true,
        currentRange: this.radarCurrentRange,
        maxRange,
        scanProgress,
        holdTime: this.radarHoldTime,
        scanComplete: this.radarScanComplete,
        isCharging: false,
        chargeRemaining: 0,
      });

      // Throttled request to server for entities in range
      const now = Date.now();
      if (now - this.radarLastRequestTime >= this.radarRequestInterval) {
        this.radarLastRequestTime = now;
        emitGameEvent('radarScanRequest', {
          x: this.x,
          y: this.y,
          range: this.radarCurrentRange,
        });
      }

      // Scan just completed
      if (this.radarScanComplete && !wasComplete) {
        emitGameEvent('radarScanRequest', {
          x: this.x,
          y: this.y,
          range: maxRange,
        });
        this.radarBlastTriggered = true;
      }
    } else {
      // Key released - stop scanning
      if (this._isScanning) {
        const firstTimeThreshold = times[0];
        const isValidScan = this.radarHoldTime >= firstTimeThreshold;

        if (isValidScan) {
          this.radarIsCharging = true;
          this.radarChargeRemaining = this.RADAR_COOLDOWN;

          if (!this.radarScanComplete) {
            emitGameEvent('radarScanRequest', {
              x: this.x,
              y: this.y,
              range: this.radarCurrentRange,
            });
            this.radarBlastTriggered = true;
          }
        }

        this._isScanning = false;
        this.radarHoldTime = 0;
        this.radarCurrentRange = 0;
        this.radarScanComplete = false;

        emitGameEvent('radarScanUpdate', {
          isScanning: false,
          hasRadar: true,
          currentRange: 0,
          maxRange,
          scanProgress: 0,
          holdTime: 0,
          scanComplete: false,
          isCharging: this.radarIsCharging,
          chargeRemaining: Math.ceil(this.radarChargeRemaining),
          validScan: isValidScan,
        });
      } else {
        emitGameEvent('radarScanUpdate', {
          isScanning: false,
          hasRadar: true,
          currentRange: 0,
          maxRange,
          scanProgress: 0,
          holdTime: 0,
          scanComplete: false,
          isCharging: false,
          chargeRemaining: 0,
        });
      }
    }

    // Scanner SFX: loop while scanning
    if (this._isScanning && !this._wasScanningLastFrame) {
      audioManager.playLoopSFX('scanner');
    } else if (!this._isScanning && this._wasScanningLastFrame) {
      audioManager.stopLoopSFX('scanner');
    }
    this._wasScanningLastFrame = this._isScanning;
  }

  /**
   * Update radar wave animation effect.
   * Draws expanding concentric circles from the ship while scanning.
   * Wave size corresponds to current scan range (grows through tier breakpoints).
   * On scan complete, fires a big "blast" wave effect.
   */
  private updateRadarWaves(delta: number): void {
    if (!this._radarWaveGraphics) return;

    const sensor = this.getEquippedSensor();
    const deltaSeconds = delta / 1000;

    // Combat-overhaul: use sensor stats or native defaults
    const scanRanges = sensor?.stats.radarScanRanges ?? Player.NATIVE_RADAR_RANGES;
    const scanTimes  = sensor?.stats.radarScanTimes  ?? Player.NATIVE_RADAR_TIMES;

    // Check if we need to spawn blast waves (scan just completed)
    if (this.radarBlastTriggered) {
      this.radarBlastTriggered = false;
      const maxRange = scanRanges[scanRanges.length - 1];
      const blastMaxRadius = maxRange * 0.7; // Bigger than normal waves

      // Spawn 2 blast waves with slight delay effect
      this.radarWaves.push({
        radius: 30,
        maxRadius: blastMaxRadius,
        alpha: 0.9,
        isBlast: true,
      });
      this.radarWaves.push({
        radius: 20,
        maxRadius: blastMaxRadius * 1.1, // Second wave goes even further
        alpha: 0.7,
        isBlast: true,
      });
    }

    // If not scanning and no waves left, clear and return
    if (!this._isScanning) {
      if (this.radarWaves.length === 0) {
        this._radarWaveGraphics.clear();
        this.radarWaveTimer = 0;
        return;
      }
    }

    const times = scanTimes;

    // Spawn new waves periodically while actively scanning (not complete)
    if (this._isScanning && !this.radarScanComplete) {
      this.radarWaveTimer += delta;
      if (this.radarWaveTimer >= this.RADAR_WAVE_INTERVAL) {
        this.radarWaveTimer = 0;

        // Spawn a wave that expands to current scan range
        this.radarWaves.push({
          radius: 30,
          maxRadius: Math.max(80, this.radarCurrentRange * 0.6),
          alpha: 0.6,
        });
      }
    }

    // Update existing waves
    this.radarWaves = this.radarWaves.filter(wave => {
      // Blast waves expand faster and have different fade
      const expandSpeed = wave.isBlast ? 300 : 150;
      wave.radius += expandSpeed * deltaSeconds;

      // Fade out as wave expands
      const baseFade = wave.isBlast ? 0.9 : 0.6;
      wave.alpha = Math.max(0, baseFade * (1 - wave.radius / wave.maxRadius));
      return wave.radius < wave.maxRadius && wave.alpha > 0.01;
    });

    // Draw all waves
    this._radarWaveGraphics.clear();

    // Calculate wave color based on scan progress
    const scanProgress = this.radarHoldTime / times[times.length - 1];
    const pulseIntensity = 0.7 + Math.sin(performance.now() / 200) * 0.3;

    // Determine color based on which tier breakpoint we're at
    let waveColor = 0x22c55e; // Green for T1 range
    if (this.radarHoldTime > times[1]) {
      waveColor = 0x3b82f6; // Blue for T3 range
    } else if (this.radarHoldTime > times[0]) {
      waveColor = 0x20a5a6; // Teal for T2 range
    }

    for (const wave of this.radarWaves) {
      // Blast waves are white/bright
      const color = wave.isBlast ? 0xffffff : waveColor;
      const lineWidth = wave.isBlast ? 4 : 2;

      // Draw concentric ring
      this._radarWaveGraphics.circle(this.x, this.y, wave.radius);
      this._radarWaveGraphics.stroke({
        width: lineWidth,
        color: color,
        alpha: wave.alpha * pulseIntensity,
      });

      // Inner glow ring
      if (wave.radius > 40) {
        this._radarWaveGraphics.circle(this.x, this.y, wave.radius - 5);
        this._radarWaveGraphics.stroke({
          width: wave.isBlast ? 2 : 1,
          color: wave.isBlast ? waveColor : 0xffffff,
          alpha: wave.alpha * (wave.isBlast ? 0.5 : 0.3) * pulseIntensity,
        });
      }
    }

    // Draw current scan range indicator (faint circle showing max detected range)
    if (this._isScanning && this.radarCurrentRange > 0) {
      const visualRange = this.radarCurrentRange * 0.6;
      this._radarWaveGraphics.circle(this.x, this.y, visualRange);
      this._radarWaveGraphics.stroke({
        width: 1,
        color: waveColor,
        alpha: 0.15 + scanProgress * 0.1,
      });
    }

  }

  /**
   * Visual effect for active warp disruptor - pulsing waves expanding to range.
   * Pink/magenta rings emanate from ship to disruptor range for the duration.
   */
  private updateDisruptorEffect(delta: number): void {
    if (!this._disruptorGraphics) return;

    const now = Date.now();
    const deltaSeconds = delta / 1000;
    const isActive = this.disruptorActiveUntil > now && this.disruptorRange > 0;

    // If not active and no waves left, clear
    if (!isActive && this.disruptorWaves.length === 0) {
      this._disruptorGraphics.clear();
      this.disruptorWaveTimer = 0;
      return;
    }

    // Spawn waves while active
    if (isActive) {
      this.disruptorWaveTimer += delta;
      if (this.disruptorWaveTimer >= this.DISRUPTOR_WAVE_INTERVAL) {
        this.disruptorWaveTimer = 0;
        this.disruptorWaves.push({
          radius: 20,
          maxRadius: this.disruptorRange,
          alpha: 0.7,
        });
      }
    }

    // Update existing waves
    this.disruptorWaves = this.disruptorWaves.filter(wave => {
      wave.radius += 200 * deltaSeconds; // Expand speed
      wave.alpha = Math.max(0, 0.7 * (1 - wave.radius / wave.maxRadius));
      return wave.radius < wave.maxRadius && wave.alpha > 0.01;
    });

    // Draw
    this._disruptorGraphics.clear();

    const pulseIntensity = 0.7 + Math.sin(performance.now() / 150) * 0.3;
    const disruptorColor = 0xff4488; // Pink/magenta

    // Draw expanding wave rings
    for (const wave of this.disruptorWaves) {
      this._disruptorGraphics.circle(this.x, this.y, wave.radius);
      this._disruptorGraphics.stroke({
        width: 2,
        color: disruptorColor,
        alpha: wave.alpha * pulseIntensity,
      });

      // Inner glow
      if (wave.radius > 30) {
        this._disruptorGraphics.circle(this.x, this.y, wave.radius - 4);
        this._disruptorGraphics.stroke({
          width: 1,
          color: 0xff88aa,
          alpha: wave.alpha * 0.3 * pulseIntensity,
        });
      }
    }

    // Draw static range boundary circle while active (faint pulsing border)
    if (isActive) {
      const remaining = (this.disruptorActiveUntil - now) / 1000;
      const fadeOut = Math.min(1, remaining); // Fade in last second

      this._disruptorGraphics.circle(this.x, this.y, this.disruptorRange);
      this._disruptorGraphics.stroke({
        width: 1.5,
        color: disruptorColor,
        alpha: 0.2 * pulseIntensity * fadeOut,
      });
    }
  }

  /**
   * Called when server confirms disruptor activation - starts the visual effect.
   */
  public startDisruptorEffect(duration: number, range: number): void {
    this.disruptorActiveUntil = Date.now() + duration;
    this.disruptorRange = range;
    this.disruptorWaves = [];
    this.disruptorWaveTimer = 0;
    // Spawn first wave immediately
    this.disruptorWaves.push({ radius: 20, maxRadius: range, alpha: 0.9 });
  }

  // Fire weapon - dispatches to appropriate method based on weapon type
  public fireWeapon(): void {
    // Check if in safe zone (weapons disabled)
    if (this.inSafeZone) {
      return;
    }

    // Hard-flux vent lock: any heat-generating action is blocked during a
    // voluntary vent (server rejects too — gate locally so visuals match).
    if (this.isVenting) {
      return;
    }

    const weapon = this.getEquippedWeapon();
    if (!weapon) {
      this.showMissingModuleAlert('weapon');
      return;
    }

    // Handle weapon type (energy ≡ laser, explosive ≡ missile in legacy terms)
    switch (weapon.weaponType) {
      case 'laser':
      case 'energy':
        this.startLaserFire(weapon);
        break;
      case 'kinetic':
      case 'missile':
      case 'explosive':
        this.fireProjectile(weapon);
        break;
    }
  }

  // Start continuous laser fire (called when fire button pressed with laser equipped)
  private startLaserFire(weapon: Weapon): void {
    if (this.isShipOverheated) return;
    if (this.isVenting) return;

    this.isLaserFiring = true;
    // CombatLaser is managed by GameScene, emit event to start it
    emitGameEvent('startLaserFire', {
      weapon,
      playerId: (this.getData('playerId') as string) || 'local',
    });
    // Combat-overhaul: notify server to accrue continuous beam heat
    try { networkManager.sendBeamStart(); } catch { /* offline mode */ }
  }

  // Stop laser fire (called when fire button released)
  public stopLaserFire(): void {
    if (this.isLaserFiring) {
      this.isLaserFiring = false;
      emitGameEvent('stopLaserFire', undefined);
      try { networkManager.sendBeamEnd(); } catch { /* offline mode */ }
    }
  }

  // Check if laser is currently firing
  public isLaserCurrentlyFiring(): boolean {
    return this.isLaserFiring;
  }

  // Fire projectile weapon (kinetic/explosive)
  private fireProjectile(weapon: Weapon): void {
    // Server controls overheat — block locally only if server says overloaded
    if (this.isShipOverheated) return;
    if (this.isVenting) return;

    // Fire rate throttle (client-side, prevents spamming the server)
    const now = Date.now();
    const fireDelay = 1000 / weapon.stats.fireRate;
    if (now - this.lastFireTime < fireDelay) return;
    this.lastFireTime = now;

    // Calculate projectile spawn position (front of ship)
    const noseAngle = degToRad(this._angle - 90); // Ship nose direction
    const noseDistance = this.noseOffset;
    const spawnX = this.x + Math.cos(noseAngle) * noseDistance;
    const spawnY = this.y + Math.sin(noseAngle) * noseDistance;

    // Notify server of weapon fire (heat accrues on fire, not on hit)
    try { networkManager.sendWeaponFired(); } catch { /* offline */ }

    // Emit event to create projectile (GameScene will handle this)
    emitGameEvent('fireWeapon', {
      x: spawnX,
      y: spawnY,
      angle: this._angle - 90, // Ship nose direction
      weapon: weapon,
      playerId: (this.getData('playerId') as string) || 'local',
      shipVelocityX: this.velocityX,
      shipVelocityY: this.velocityY,
      targetId: this.selectedTarget?.id,
      targetType: this.selectedTarget?.type,
    });
  }

  // Start missile reload
  // Set the selected target for missile tracking (called by GameScene)
  public setSelectedTarget(target: { id: string; type: 'bot' | 'player' | 'asteroid' | 'drone'; x?: number; y?: number } | null): void {
    this.selectedTarget = target;
  }

  // Get the selected target
  public getSelectedTarget(): { id: string; type: 'bot' | 'player' | 'asteroid' | 'drone' } | null {
    return this.selectedTarget;
  }

  // Ship-level heat getters (server-authoritative values)
  public getWeaponHeat(): number {
    return this.shipHeat;
  }

  public isWeaponOverheated(): boolean {
    return this.isShipOverheated;
  }

  // Legacy ammo getters — no-op under combat overhaul (heat-only, no magazines)
  public getAmmoCount(): number { return 0; }
  public getMagazineSize(): number { return 0; }
  public getIsReloading(): boolean { return false; }
  public getReloadProgress(): number { return 1; }
  public isMissileWeapon(): boolean {
    const w = this.getEquippedWeapon();
    return w?.weaponType === 'missile' || w?.weaponType === 'explosive';
  }

  // Show alert for missing module (with cooldown)
  private showMissingModuleAlert(moduleName: string): void {
    const now = Date.now();
    if (now - this.lastAlertTime < this.alertCooldown) return;

    this.lastAlertTime = now;
    emitGameEvent('notification', {
      message: `No ${moduleName} equipped!`,
      type: 'warning',
    });
  }

  // Handle mouse-based rotation (for keys_mouse control scheme)
  private handleMouseRotation(): void {
    if (!this.inputManager || !this.camera) return;

    const pointer = this.inputManager.pointer;

    // Get mouse position in world coordinates
    const worldX = this.camera.getWorldX(pointer.x);
    const worldY = this.camera.getWorldY(pointer.y);

    // Calculate angle from ship to mouse
    const dx = worldX - this.x;
    const dy = worldY - this.y;
    const targetAngle = radToDeg(Math.atan2(dy, dx)) + 90; // +90 because ship points up

    // Smoothly rotate towards target angle
    const currentAngle = this._angle;
    let angleDiff = targetAngle - currentAngle;

    // Normalize angle difference to -180 to 180
    while (angleDiff > 180) angleDiff -= 360;
    while (angleDiff < -180) angleDiff += 360;

    // Apply rotation with smoothing (instant for now, can add lerp later)
    const rotationSpeed = this.effectiveStats.rotationSpeed * 0.05; // Degrees per frame
    if (Math.abs(angleDiff) < rotationSpeed) {
      this._angle = targetAngle;
    } else {
      this._angle += Math.sign(angleDiff) * rotationSpeed;
    }
    this.rotation = degToRad(this._angle);

    // No angular velocity in mouse mode - direct control
    this.angularVelocity = 0;
  }

  private handleMovement(delta: number): void {
    // Handle autopilot movement if active
    if (this._isAutopiloting) {
      this.handleAutopilotMovement(delta);
      return;
    }

    // Skip all input if disabled (e.g., inventory open) or stunned
    if (!this.inputEnabled || !this.canAcceptInput()) {
      this.angularVelocity = 0;
      this.acceleration.x = 0;
      this.acceleration.y = 0;
      this.isThrustingForward = false;
      this._isStrafing = false;
      this._isBoosting = false;
      this.shipGraphics.thrusting = false;
      return;
    }

    const stats = this.effectiveStats;

    // Handle boost input and state (energy-based sprint system)
    const boostKeyPressed = this.isActionPressed('boost');
    const booster = this.getEquippedBooster();
    const deltaSeconds = delta / 1000;

    // Calculate boost multiplier
    let boostMultiplier = 1.0;

    // Combat-overhaul: boost is NATIVE to every ship. Thruster Booster
    // enhancer amplifies it but is not required. We always run the boost
    // pipeline — using native defaults when no booster is equipped.
    {
      // Booster stats if equipped, else native defaults (weaker than T1).
      const boostDurationMs = booster?.stats.boostDuration ?? 3000;
      const boostCooldownMs = booster?.stats.boostCooldown ?? 6000;
      const boostMult = booster?.stats.boostSpeedMultiplier ?? 1.5;
      this.boostDrainRate = this.boostMaxEnergy / (boostDurationMs / 1000);
      this.boostRechargeRate = this.boostMaxEnergy / (boostCooldownMs / 1000);

      // During autopilot, _isBoosting is controlled by handleAutopilotMovement
      // Otherwise, use manual key-based boost
      if (this._isAutopiloting) {
        // Autopilot controls boosting - drain if boosting, recharge if not
        if (this._isBoosting && this.boostEnergyTarget > 0) {
          this.boostEnergyTarget = Math.max(0, this.boostEnergyTarget - this.boostDrainRate * deltaSeconds);
          boostMultiplier = boostMult;
          if (this.boostEnergyTarget <= 0) {
            this._isBoosting = false;
          }
        } else if (!this._isBoosting) {
          this.boostEnergyTarget = Math.min(this.boostMaxEnergy, this.boostEnergyTarget + this.boostRechargeRate * deltaSeconds);
        }
      } else if (boostKeyPressed && this.boostEnergyTarget > 0) {
        // Manual boost: can boost if holding key and have energy
        // Need minimum energy to START boosting, but can continue until empty
        if (this._isBoosting || this.boostEnergyTarget >= this.boostMinToStart) {
          this._isBoosting = true;
          // Drain energy while boosting
          this.boostEnergyTarget = Math.max(0, this.boostEnergyTarget - this.boostDrainRate * deltaSeconds);
          boostMultiplier = boostMult;

          // Stop boosting when energy depleted
          if (this.boostEnergyTarget <= 0) {
            this._isBoosting = false;
          }
        }
      } else {
        // Not boosting - recharge energy
        this._isBoosting = false;
        this.boostEnergyTarget = Math.min(this.boostMaxEnergy, this.boostEnergyTarget + this.boostRechargeRate * deltaSeconds);
      }

      // Display value tracks target directly — drain/recharge is already per-frame smooth
      this.boostEnergy = this.boostEnergyTarget;
    }

    // Boost SFX: start/stop loop on state change
    if (this._isBoosting && !this._wasBoostingLastFrame) {
      audioManager.playLoopSFX('boost');
    } else if (!this._isBoosting && this._wasBoostingLastFrame) {
      audioManager.stopLoopSFX('boost');
    }
    this._wasBoostingLastFrame = this._isBoosting;

    // Mouse controls rotation - ship always faces mouse pointer
    this.handleMouseRotation();

    // Thrust inputs - use bound action keys
    const thrust = this.isActionPressed('accelerate');
    const reverse = this.isActionPressed('reverse');
    const brake = this.isActionPressed('brake');
    const strafeLeftInput = this.isActionPressed('strafeLeft');
    const strafeRightInput = this.isActionPressed('strafeRight');

    // Ship graphics are drawn with nose pointing UP
    // Container angle 0 = up in our system, so thrust angle needs adjustment
    const thrustAngle = degToRad(this._angle - 90);

    // Calculate total acceleration from all thrust sources
    let accelX = 0;
    let accelY = 0;

    // Forward thrust - works without engine but severely penalized (emergency thrusters)
    if (thrust) {
      if (!this.hasEngine()) {
        this.showMissingModuleAlert('engine');
      }
      // Apply thrust (emergency stats are already calculated if no engine)
      // Apply boost multiplier to acceleration when boosting
      const acceleration = stats.acceleration * boostMultiplier;
      accelX += Math.cos(thrustAngle) * acceleration;
      accelY += Math.sin(thrustAngle) * acceleration;
      this.isThrustingForward = true;
    } else {
      this.isThrustingForward = false;
    }

    // Reverse thrust (accelerate backward) - works without engine but penalized
    if (reverse && !brake) {
      if (!this.hasEngine()) {
        this.showMissingModuleAlert('engine');
      }
      // Accelerate in opposite direction (backward) - same acceleration as forward
      accelX -= Math.cos(thrustAngle) * stats.acceleration;
      accelY -= Math.sin(thrustAngle) * stats.acceleration;
      this.isReversing = true;
    } else {
      this.isReversing = false;
    }

    // Brake/stabilize - decelerates in all directions to stop the ship
    if (brake) {
      const speed = Math.sqrt(this.velocityX ** 2 + this.velocityY ** 2);
      if (speed > 5) {
        // Apply deceleration opposite to current velocity direction
        const velAngle = Math.atan2(this.velocityY, this.velocityX);
        const brakeForce = Math.min(stats.brakingPower, speed * 2); // Stronger braking at higher speeds
        accelX -= Math.cos(velAngle) * brakeForce;
        accelY -= Math.sin(velAngle) * brakeForce;
        this.isBraking = true;
      } else {
        // Almost stopped - just zero out velocity
        this.velocityX = 0;
        this.velocityY = 0;
        this.isBraking = false;
      }
    } else {
      this.isBraking = false;
    }

    // Brake SFX: loop while braking with velocity
    if (this.isBraking && !this._wasBrakingLastFrame) {
      audioManager.playLoopSFX('brake');
    } else if (!this.isBraking && this._wasBrakingLastFrame) {
      audioManager.stopLoopSFX('brake');
    }
    this._wasBrakingLastFrame = this.isBraking;

    // Strafe (side movement) - now built into engine, not separate slots
    if (stats.strafeAcceleration > 0 && this.hasEngine()) {
      const strafeAngle = thrustAngle + Math.PI / 2; // +90 degrees perpendicular

      if (strafeLeftInput) {
        accelX += Math.cos(strafeAngle) * -stats.strafeAcceleration;
        accelY += Math.sin(strafeAngle) * -stats.strafeAcceleration;
        this._isStrafing = true;
      } else if (strafeRightInput) {
        accelX += Math.cos(strafeAngle) * stats.strafeAcceleration;
        accelY += Math.sin(strafeAngle) * stats.strafeAcceleration;
        this._isStrafing = true;
      } else {
        this._isStrafing = false;
      }
    } else {
      // No engine equipped or no strafe capability
      if (strafeLeftInput || strafeRightInput) {
        if (!this.hasEngine()) {
          this.showMissingModuleAlert('engine');
        }
      }
      this._isStrafing = false;
    }

    // Apply combined acceleration
    this.acceleration.x = accelX;
    this.acceleration.y = accelY;

    // Apply boost to max speed (temporarily increase during boost)
    if (this._isBoosting) {
      this.currentMaxVelocity = this.effectiveStats.maxSpeed * boostMultiplier;
    } else {
      this.currentMaxVelocity = this.effectiveStats.maxSpeed;
    }

    // Update thrust visual
    this.shipGraphics.thrusting = this.isThrustingForward;

    // Boost intensifies the engine flare
    this.shipGraphics.setFlareIntensity(this._isBoosting ? 1.3 : 1.0);
  }

  private updateStrafeThrust(): void {
    // Update strafe thrust flares
    // Newton's 3rd law: Thruster fires opposite to movement direction
    // Strafe left = right thruster fires
    // Strafe right = left thruster fires
    const strafeLeftInput = this.isActionPressed('strafeLeft');
    const strafeRightInput = this.isActionPressed('strafeRight');

    // A key (move left) -> RIGHT thruster fires
    if (strafeLeftInput && this.hasEngine() && this.strafeRightFlare) {
      this.strafeRightFlare.show();
      this.strafeRightFlare.update();
    } else if (this.strafeRightFlare) {
      this.strafeRightFlare.hide();
    }

    // D key (move right) -> LEFT thruster fires
    if (strafeRightInput && this.hasEngine() && this.strafeLeftFlare) {
      this.strafeLeftFlare.show();
      this.strafeLeftFlare.update();
    } else if (this.strafeLeftFlare) {
      this.strafeLeftFlare.hide();
    }
  }

  private updateReverseThrust(): void {
    if (this.isReversing && this.hasEngine()) {
      if (this.reverseLeftFlare) {
        this.reverseLeftFlare.show();
        this.reverseLeftFlare.update();
      }
      if (this.reverseRightFlare) {
        this.reverseRightFlare.show();
        this.reverseRightFlare.update();
      }
    } else {
      if (this.reverseLeftFlare) this.reverseLeftFlare.hide();
      if (this.reverseRightFlare) this.reverseRightFlare.hide();
    }
  }

  private updateBrakeSparks(delta: number): void {
    if (!this.brakeGraphics) return;

    // Update lifetimes and remove expired lines
    this.brakeLineData = this.brakeLineData.filter(data => {
      data.life -= delta;
      return data.life > 0;
    });

    // Spawn new speed lines when braking
    if (this.isBraking) {
      this.brakeLineTimer += delta;

      // Spawn lines every ~80ms (staggered appearance)
      if (this.brakeLineTimer >= 80) {
        this.brakeLineTimer = 0;
        this.spawnBrakeLines();
      }
    }

    // Redraw all brake lines on single graphics object
    this.brakeGraphics.clear();

    for (const data of this.brakeLineData) {
      const alpha = (data.life / data.maxLife) * (0.6 - data.layer * 0.15);
      if (alpha <= 0) continue;

      const points: { x: number; y: number }[] = [];
      const segments = 8;
      for (let j = 0; j <= segments; j++) {
        const t = j / segments;
        const angle = (t - 0.5) * Math.PI * 0.8;

        const perpOffset = Math.sin(angle) * (data.width / 2);
        const depthOffset = Math.cos(angle) * data.depth;

        const perpAngle = data.angle + Math.PI / 2;
        const px = data.x + Math.cos(perpAngle) * perpOffset + Math.cos(data.angle) * depthOffset;
        const py = data.y + Math.sin(perpAngle) * perpOffset + Math.sin(data.angle) * depthOffset;

        points.push({ x: px, y: py });
      }

      // Draw the path
      if (points.length > 1) {
        this.brakeGraphics.moveTo(points[0].x, points[0].y);
        for (let i = 1; i < points.length; i++) {
          this.brakeGraphics.lineTo(points[i].x, points[i].y);
        }
        this.brakeGraphics.stroke({ width: 1, color: 0x20a5a6, alpha });
      }
    }
  }

  private spawnBrakeLines(): void {
    const speed = this.getSpeed();
    if (speed < 30) return;

    const velAngle = Math.atan2(this.velocityY, this.velocityX);
    // Gentle scaling: sqrt dampens large ships, clamped to reasonable range
    const shipScale = Math.min(1.8, Math.max(0.85, Math.sqrt(this.noseOffset / 20)));
    const arcWidth = (120 + speed * 0.3) * shipScale;
    const distances = [50 * shipScale, 85 * shipScale];

    for (let i = 0; i < distances.length; i++) {
      const aheadDistance = distances[i] + Math.random() * 10;
      const lifetime = 250 + Math.random() * 100;

      this.brakeLineData.push({
        x: this.x + Math.cos(velAngle) * aheadDistance,
        y: this.y + Math.sin(velAngle) * aheadDistance,
        angle: velAngle,
        width: arcWidth,
        depth: (12 + i * 6) * shipScale,
        life: lifetime,
        maxLife: lifetime,
        layer: i,
      });
    }
  }

  /**
   * Combat-overhaul: draw the natural shield field around the ship while
   * raised. Expansion lerps from 0 → shieldRadius over shieldExpansionMs.
   * Lower is instantaneous (cleared on the next frame after shieldActive flips).
   */
  private updateNaturalShieldVisual(delta: number): void {
    const g = this._overhaulVfxGraphics;
    if (!g) return;
    g.clear();

    const active = (this as any)._shieldActive as boolean | undefined;
    if (!active) return;

    const raisedAt = (this as any)._shieldRaisedAt as number | undefined;
    const radius = ((this as any)._shieldRadius as number | undefined) ?? 80;
    const expansion = ((this as any)._shieldExpansionMs as number | undefined) ?? 300;
    const coverageDeg = this.shipClass?.stats?.shieldCoverage ?? 360;

    const sinceRaise = raisedAt ? Date.now() - raisedAt : expansion;
    const t = Math.min(1, sinceRaise / Math.max(1, expansion));
    const currentRadius = radius * t;

    if (currentRadius < 4) return;

    // Apply vibration offset when shield is hit
    let cx = this.x;
    let cy = this.y;
    let strokeAlpha = 0.45;
    let strokeWidth = 1.5;
    if (this._shieldVibrateTime > 0) {
      this._shieldVibrateTime -= delta;
      const intensity = Math.min(1, this._shieldVibrateTime / 300) * 4;
      this._shieldVibrateOffsetX = (Math.random() - 0.5) * 2 * intensity;
      this._shieldVibrateOffsetY = (Math.random() - 0.5) * 2 * intensity;
      cx += this._shieldVibrateOffsetX;
      cy += this._shieldVibrateOffsetY;
      strokeAlpha = 0.45 + 0.4 * (this._shieldVibrateTime / 300);
      strokeWidth = 1.5 + 1.5 * (this._shieldVibrateTime / 300);
    }

    if (coverageDeg >= 360) {
      // Full circle for 360° coverage (dreadnought)
      g.circle(cx, cy, currentRadius);
      g.fill({ color: 0x66ccff, alpha: 0.06 });
      g.circle(cx, cy, currentRadius);
      g.stroke({ width: strokeWidth, color: 0x88ddff, alpha: strokeAlpha });
    } else {
      // Partial arc facing the ship's nose direction
      const noseAngle = degToRad(this._angle - 90);
      const halfArc = (coverageDeg / 2) * (Math.PI / 180);
      const startAngle = noseAngle - halfArc;
      const endAngle = noseAngle + halfArc;

      // Filled wedge (move to center, arc, close)
      g.moveTo(cx, cy);
      g.arc(cx, cy, currentRadius, startAngle, endAngle);
      g.closePath();
      g.fill({ color: 0x66ccff, alpha: 0.06 });

      // Arc stroke (just the curved edge)
      g.arc(cx, cy, currentRadius, startAngle, endAngle);
      g.stroke({ width: strokeWidth, color: 0x88ddff, alpha: strokeAlpha });
    }
  }

  private _wasOverloaded: boolean = false;

  /**
   * Combat-overhaul: orange pulsing circle around ship + ship SVG color
   * cycling while overloaded. Circle scales to ship size.
   * Movement is still allowed, but weapons and shield are offline.
   */
  private updateOverloadVisual(delta: number): void {
    const g = this._overhaulVfxGraphics;
    if (!g) return;
    const overloaded = this.isShipOverheated;

    // Reset tint when overload ends
    if (!overloaded) {
      if (this._wasOverloaded) {
        this.shipGraphics.setOverloadTint(0xffffff);
        this._wasOverloaded = false;
      }
      return;
    }
    this._wasOverloaded = true;

    this._overloadPhase += delta * 0.006;
    const phase = this._overloadPhase;

    // Cycle through red → orange → yellow using a triangle wave
    // t goes 0→1→0 over time, we use it to pick from the gradient
    const cycle = (phase * 1.5) % 3; // 0-3 range, each color gets a segment
    let r: number, gCh: number, b: number;
    if (cycle < 1) {
      // Red (0xff,0x22,0x00) → Orange (0xff,0x88,0x00)
      const t = cycle;
      r = 0xff; gCh = Math.round(0x22 + (0x88 - 0x22) * t); b = 0x00;
    } else if (cycle < 2) {
      // Orange (0xff,0x88,0x00) → Yellow (0xff,0xdd,0x22)
      const t = cycle - 1;
      r = 0xff; gCh = Math.round(0x88 + (0xdd - 0x88) * t); b = Math.round(0x22 * t);
    } else {
      // Yellow (0xff,0xdd,0x22) → Red (0xff,0x22,0x00)
      const t = cycle - 2;
      r = 0xff; gCh = Math.round(0xdd - (0xdd - 0x22) * t); b = Math.round(0x22 * (1 - t));
    }
    this.shipGraphics.setOverloadTint((r << 16) | (gCh << 8) | b);
  }

  /**
   * Combat-overhaul: hot-air particle effect while venting.
   * White/warm particles radiate outward from the ship like heat dissipating,
   * inspired by the warp calculation effect but reversed (outward instead of inward).
   */
  private updateVentingEffect(delta: number): void {
    const g = this._overhaulVfxGraphics;
    if (!g) return;

    const active = this.isVenting || this.isShipOverheated;

    if (!active) {
      // Clean up when venting stops
      if (this._ventParticles.length > 0) {
        this._ventParticles = [];
        this._ventTime = 0;
      }
      return;
    }

    this._ventTime += delta * 0.004;
    const t = this._ventTime;

    const maxParticles = 40;
    const spawnRadius = 15;     // start near ship hull
    const maxRadius = 140;      // fade out distance

    // Spawn new particles to maintain count
    while (this._ventParticles.length < maxParticles) {
      const angle = Math.random() * Math.PI * 2;
      this._ventParticles.push({
        angle,
        dist: spawnRadius + Math.random() * 10,
        speed: 0.6 + Math.random() * 1.2,
        size: 0.6 + Math.random() * 1.8,
        alpha: 0.5 + Math.random() * 0.4,
        drift: (Math.random() - 0.5) * 0.015,
        life: 0,
        maxLife: 0.8 + Math.random() * 0.4,
      });
    }

    // Update and render particles
    for (let i = this._ventParticles.length - 1; i >= 0; i--) {
      const p = this._ventParticles[i];

      // Move outward
      p.dist += p.speed;
      p.life += 0.012;

      // Slight angular drift (hot air wobble)
      p.angle += p.drift + Math.sin(t * 3 + p.angle * 5) * 0.004;

      // Decelerate as particles spread out (dissipating energy)
      p.speed *= 0.995;

      // Recycle when too far or lifetime exceeded
      if (p.dist > maxRadius || p.life > p.maxLife) {
        const angle = Math.random() * Math.PI * 2;
        p.angle = angle;
        p.dist = spawnRadius + Math.random() * 10;
        p.speed = 0.6 + Math.random() * 1.2;
        p.size = 0.6 + Math.random() * 1.8;
        p.alpha = 0.5 + Math.random() * 0.4;
        p.drift = (Math.random() - 0.5) * 0.015;
        p.life = 0;
        p.maxLife = 0.8 + Math.random() * 0.4;
        continue;
      }

      const px = this.x + Math.cos(p.angle) * p.dist;
      const py = this.y + Math.sin(p.angle) * p.dist;

      // Alpha: bright near ship, fades with distance
      const normalizedDist = (p.dist - spawnRadius) / (maxRadius - spawnRadius);
      const distFade = 1 - normalizedDist;
      // Also fade in at birth and out near end of life
      const lifeFade = p.life < 0.15
        ? p.life / 0.15
        : p.life > p.maxLife * 0.7
          ? (p.maxLife - p.life) / (p.maxLife * 0.3)
          : 1;
      const alpha = p.alpha * distFade * lifeFade * 0.7;

      if (alpha <= 0.01) continue;

      // Draw wispy trail (short line outward from particle)
      const trailLen = 6 + p.speed * 4;
      const trailX = px - Math.cos(p.angle) * trailLen;
      const trailY = py - Math.sin(p.angle) * trailLen;
      g.moveTo(trailX, trailY);
      g.lineTo(px, py);
      g.stroke({ width: p.size * 0.5, color: 0xffffff, alpha: alpha * 0.3 });

      // Draw particle dot — white core
      const pSize = p.size + Math.sin(t * 6 + p.angle * 4) * 0.3;
      g.circle(px, py, pSize);
      g.fill({ color: 0xffffff, alpha: alpha * 0.9 });

      // Warm outer glow on larger particles
      if (p.size > 1.2) {
        g.circle(px, py, pSize * 1.8);
        g.fill({ color: 0xffddaa, alpha: alpha * 0.15 });
      }
    }

    // Subtle shimmer halo around ship center (heat haze)
    const hazeR = 20 + Math.sin(t * 5) * 4;
    g.circle(this.x, this.y, hazeR + 10);
    g.fill({ color: 0xffeecc, alpha: 0.02 + Math.sin(t * 7) * 0.01 });
    g.circle(this.x, this.y, hazeR);
    g.fill({ color: 0xffffff, alpha: 0.04 + Math.sin(t * 9) * 0.015 });
  }

  private updateShieldHitArcs(delta: number): void {
    if (!this.shieldGraphics) return;

    // Update lifetimes and remove expired arcs
    this.shieldArcData = this.shieldArcData.filter(data => {
      data.life -= delta;
      return data.life > 0;
    });

    // Redraw all shield arcs on single graphics object
    this.shieldGraphics.clear();

    for (const data of this.shieldArcData) {
      const progress = 1 - (data.life / data.maxLife);
      const alpha = (1 - progress) * 0.8;
      const scale = 1 + progress * 0.3;
      if (alpha <= 0) continue;

      const points: { x: number; y: number }[] = [];
      const segments = 6;
      for (let j = 0; j <= segments; j++) {
        const t = j / segments;
        const angle = (t - 0.5) * Math.PI * 0.6;

        const perpOffset = Math.sin(angle) * (data.width / 2) * scale;
        const depthOffset = Math.cos(angle) * data.depth * scale;

        const perpAngle = data.angle + Math.PI / 2;
        const px = data.x + Math.cos(perpAngle) * perpOffset - Math.cos(data.angle) * depthOffset;
        const py = data.y + Math.sin(perpAngle) * perpOffset - Math.sin(data.angle) * depthOffset;

        points.push({ x: px, y: py });
      }

      // Draw the path
      if (points.length > 1) {
        this.shieldGraphics.moveTo(points[0].x, points[0].y);
        for (let i = 1; i < points.length; i++) {
          this.shieldGraphics.lineTo(points[i].x, points[i].y);
        }
        this.shieldGraphics.stroke({ width: 1, color: 0x66ccff, alpha });
      }
    }
  }

  /**
   * Compute shield drag client-side and draw a speed-bleed visual.
   * Renders flickering arcs around the ship when drag is draining shield.
   */
  private updateShieldDragVisual(delta: number): void {
    const speed = Math.sqrt(this.velocityX ** 2 + this.velocityY ** 2);
    const stats = this.effectiveStats;

    const dragPerSec = computeShieldDrag({
      currentSpeed: speed,
      maxSpeed: stats.maxSpeed,
      engineType: stats.engineType,
      shieldType: stats.shieldType,
    });

    this._shieldDragActive = dragPerSec > 0 && this.shield > 0;
    // Intensity = how far above threshold we are (0 at threshold, 1 at maxSpeed)
    if (this._shieldDragActive) {
      const threshold = stats.maxSpeed * SHIELD_DRAG_SPEED_THRESHOLD;
      this._shieldDragIntensity = Math.min(1, (speed - threshold) / (stats.maxSpeed - threshold));
    } else {
      this._shieldDragIntensity = 0;
    }

    if (!this._shieldDragActive || !this.shieldGraphics) return;

    // Advance animation phase
    this.shieldDragPhase += delta * 0.008;

    const intensity = this._shieldDragIntensity;
    const velAngle = Math.atan2(this.velocityY, this.velocityX);

    // Draw 3-5 small flickering arcs at the front of the ship (direction of travel)
    const numArcs = 3 + Math.floor(intensity * 2);
    const spread = Math.PI * (0.6 + intensity * 0.4); // wider spread at higher speed

    for (let i = 0; i < numArcs; i++) {
      const t = (i / (numArcs - 1)) - 0.5; // -0.5 to 0.5
      const arcAngle = velAngle + t * spread;

      // Flicker: each arc has its own phase offset
      const flicker = 0.4 + 0.6 * Math.abs(Math.sin(this.shieldDragPhase * 3 + i * 1.7));
      const alpha = intensity * flicker * 0.35;
      if (alpha < 0.02) continue;

      const dist = 32 + Math.sin(this.shieldDragPhase * 2 + i) * 4;
      const cx = this.x + Math.cos(arcAngle) * dist;
      const cy = this.y + Math.sin(arcAngle) * dist;

      // Small concave arc
      const segments = 4;
      const arcWidth = 18 + intensity * 12;
      const depth = 4 + intensity * 3;
      const perpAngle = arcAngle + Math.PI / 2;

      const points: { x: number; y: number }[] = [];
      for (let j = 0; j <= segments; j++) {
        const s = (j / segments - 0.5) * Math.PI * 0.5;
        const px = cx + Math.cos(perpAngle) * Math.sin(s) * (arcWidth / 2) - Math.cos(arcAngle) * Math.cos(s) * depth;
        const py = cy + Math.sin(perpAngle) * Math.sin(s) * (arcWidth / 2) - Math.sin(arcAngle) * Math.cos(s) * depth;
        points.push({ x: px, y: py });
      }

      if (points.length > 1) {
        this.shieldGraphics.moveTo(points[0].x, points[0].y);
        for (let k = 1; k < points.length; k++) {
          this.shieldGraphics.lineTo(points[k].x, points[k].y);
        }
        // Teal-to-orange shift as intensity increases
        const r = Math.floor(0x20 + intensity * (0xdd - 0x20));
        const g = Math.floor(0xa5 + intensity * (0x56 - 0xa5));
        const b = Math.floor(0xa6 + intensity * (0x39 - 0xa6));
        const color = (r << 16) | (g << 8) | b;
        this.shieldGraphics.stroke({ width: 1, color, alpha });
      }
    }
  }

  private updateVisibilityCircles(): void {
    if (!this.visibilityGraphics || !this.camera) return;

    const zoom = this.camera.zoom;

    // Only show circles when zoomed out (zoom < 0.3)
    // Fade in gradually as zoom decreases
    const ZOOM_THRESHOLD = 0.3;
    const ZOOM_FULL_EFFECT = 0.15; // Full opacity at this zoom level

    if (zoom >= ZOOM_THRESHOLD) {
      this.visibilityGraphics.clear();
      return;
    }

    // Calculate alpha based on zoom (more visible when more zoomed out)
    const t = (ZOOM_THRESHOLD - zoom) / (ZOOM_THRESHOLD - ZOOM_FULL_EFFECT);
    const alpha = Math.min(1, Math.max(0, t)) * 0.9; // Max alpha 0.9

    // Neon glow pulse effect
    const pulse = 0.85 + Math.sin(performance.now() / 400) * 0.15;

    // Circle radii for neon glow effect - scale with ship size
    const coreRadius = Math.max(60, this.noseOffset * 2);

    this.visibilityGraphics.clear();

    // Neon glow effect - multiple layered circles with decreasing alpha (outer to inner)
    const glowColor = 0x20a5a6; // Teal

    // Outermost glow layer (very faint, wide)
    this.visibilityGraphics.circle(this.x, this.y, coreRadius + 20);
    this.visibilityGraphics.stroke({ width: 12, color: glowColor, alpha: alpha * 0.1 * pulse });

    // Middle glow layer
    this.visibilityGraphics.circle(this.x, this.y, coreRadius + 10);
    this.visibilityGraphics.stroke({ width: 8, color: glowColor, alpha: alpha * 0.2 * pulse });

    // Inner glow layer
    this.visibilityGraphics.circle(this.x, this.y, coreRadius + 4);
    this.visibilityGraphics.stroke({ width: 5, color: glowColor, alpha: alpha * 0.4 * pulse });

    // Core bright ring
    this.visibilityGraphics.circle(this.x, this.y, coreRadius);
    this.visibilityGraphics.stroke({ width: 3, color: 0x40d5d6, alpha: alpha * 0.9 * pulse }); // Brighter teal core

    // Inner bright highlight
    this.visibilityGraphics.circle(this.x, this.y, coreRadius - 2);
    this.visibilityGraphics.stroke({ width: 1, color: 0xffffff, alpha: alpha * 0.3 * pulse }); // White highlight
  }

  public spawnShieldHitEffect(_hitAngle?: number): void {
    // Vibrate the shield circle for 300ms
    this._shieldVibrateTime = 300;
  }

  /**
   * Asteroids-style shield shatter: the shield circle breaks into arc fragments
   * that drift outward, rotating and fading — like shattering a glass dome.
   */
  public spawnShieldShatter(): void {
    if (!this.parent) return;
    const radius = ((this as any)._shieldRadius as number | undefined) ?? 80;
    const coverageDeg = this.shipClass?.stats?.shieldCoverage ?? 360;
    const noseAngle = degToRad(this._angle - 90);
    createShieldShatter(this.parent, this.x, this.y, radius, coverageDeg, noseAngle);
  }

  // Handle collision with asteroid - NO DAMAGE, only visual effects and camera shake
  // Per collisions.md: All collisions cause bounce + stun, but no damage
  public handleAsteroidCollision(asteroidVelocity: { x: number; y: number }, asteroidMass: number): void {
    const now = Date.now();
    if (now - this.lastCollisionTime < this.collisionCooldown) return;
    this.lastCollisionTime = now;

    // Calculate relative velocity for camera shake intensity
    const relVelX = this.velocityX - asteroidVelocity.x;
    const relVelY = this.velocityY - asteroidVelocity.y;
    const relSpeed = Math.sqrt(relVelX * relVelX + relVelY * relVelY);

    // Camera shake based on collision intensity (no damage)
    const asteroidMassFactor = Math.sqrt(asteroidMass / 100);
    const intensity = Math.min((relSpeed * asteroidMassFactor) / 300, 0.05);
    this.camera?.shake(200, intensity);
  }

  // Handle collision with another player - NO DAMAGE, only camera shake
  // Per collisions.md: All collisions cause bounce + stun, but no damage
  public handlePlayerCollision(otherVelocity: { x: number; y: number }): void {
    const now = Date.now();
    if (now - this.lastCollisionTime < this.collisionCooldown) return;
    this.lastCollisionTime = now;

    // Calculate relative velocity for camera shake intensity
    const relVelX = this.velocityX - otherVelocity.x;
    const relVelY = this.velocityY - otherVelocity.y;
    const relSpeed = Math.sqrt(relVelX * relVelX + relVelY * relVelY);

    // Camera shake based on collision intensity (no damage)
    const intensity = Math.min(relSpeed / 600, 0.01);
    this.camera?.shake(80, intensity);
  }

  // Handle collision response - NO DAMAGE, only stun and velocity change
  // Per collisions.md: All collisions cause bounce + stun, but no damage
  public handleCollision(otherX: number, otherY: number, impactSpeed: number, newVelocity: { x: number; y: number }): void {
    // Apply stun and velocity from base class (no damage)
    this.applyCollision(newVelocity);
  }

  // Apply bounce physics after collision (manual, no physics engine)
  public applyCollisionBounce(otherX: number, otherY: number, otherMass: number): void {
    // Simple elastic collision response
    const dx = this.x - otherX;
    const dy = this.y - otherY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist === 0) return;

    const nx = dx / dist;
    const ny = dy / dist;

    // Relative velocity along collision normal
    const relVelNormal = this.velocityX * nx + this.velocityY * ny;

    // Apply bounce
    const massRatio = otherMass / (this.mass + otherMass);
    const bounceImpulse = relVelNormal * this.bounce * 2 * massRatio;

    this.velocityX -= bounceImpulse * nx;
    this.velocityY -= bounceImpulse * ny;
  }

  // === GHOST OBJECT PATTERN: Server Correction ===
  /**
   * Apply server-authoritative position correction.
   * Called when server sends authoritative state (collision corrections).
   * Visual position will smoothly lerp toward this in update().
   */
  public applyServerCorrection(x: number, y: number, vx: number, vy: number): void {
    this.serverX = x;
    this.serverY = y;
    this.serverVelocityX = vx;
    this.serverVelocityY = vy;
    this.serverPositionInitialized = true;

    // CRITICAL FIX: Immediately apply server velocity when stunned (collision bounce)
    // Don't wait for lerp - the stun might end before lerp completes, causing old velocity to resume
    if (this.isStunned) {
      this.velocityX = vx;
      this.velocityY = vy;
    }

    // Mark that we have a correction to apply
    this.hasPendingServerCorrection = true;
  }

  /**
   * Apply local collision prediction (bounce + separation).
   * Server will override with authoritative correction via onPlayerHit.
   * This prevents "passing through" objects during network latency.
   */
  public applyLocalCollisionPrediction(
    newVelocity: { x: number; y: number },
    separation: { x: number; y: number }
  ): void {
    // 1. Apply bounce velocity immediately
    this.velocityX = newVelocity.x;
    this.velocityY = newVelocity.y;

    // 2. Apply proportional separation offset (from shared physics)
    this.x += separation.x;
    this.y += separation.y;
  }

  /**
   * Push this entity outside collision radius of another object.
   * Used during stun to prevent visual overlap without changing velocity.
   */
  public separateFrom(otherX: number, otherY: number, otherRadius: number): void {
    const dx = this.x - otherX;
    const dy = this.y - otherY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const minDist = this.collisionRadius + otherRadius;

    if (dist < minDist && dist > 0) {
      const nx = dx / dist;
      const ny = dy / dist;
      // Proportional push — just enough to resolve the overlap
      this.x += nx * (minDist - dist);
      this.y += ny * (minDist - dist);
    }
  }

  /**
   * Initialize server position (call once when player spawns or enters constellation).
   * Sets both server and visual position to avoid initial lerp.
   * Does NOT set hasPendingServerCorrection since this is initialization, not correction.
   */
  public initializeServerPosition(x: number, y: number, vx: number = 0, vy: number = 0): void {
    this.serverX = x;
    this.serverY = y;
    this.serverVelocityX = vx;
    this.serverVelocityY = vy;
    // Also set visual position to avoid initial lerp
    this.x = x;
    this.y = y;
    this.velocityX = vx;
    this.velocityY = vy;
    this.serverPositionInitialized = true;
  }

  // === INPUT SEQUENCING: Server Reconciliation ===
  /**
   * Update the last acknowledged input sequence from server.
   * Called when receiving state updates from server.
   * In a full implementation, this would trigger input replay for unacknowledged inputs.
   * For now, it's used for tracking synchronization state.
   */
  public updateLastAckedInput(lastProcessedInput: number): void {
    this.lastServerAckedInput = lastProcessedInput;
  }

  /**
   * Get the last input sequence that the server has acknowledged.
   * Useful for debugging network lag and synchronization issues.
   */
  public getLastServerAckedInput(): number {
    return this.lastServerAckedInput;
  }

  // Handle damage - Player-specific version with visual feedback
  // hitAngle is optional - if provided, shield effect appears at that angle
  public handleDamage(amount: number, hitAngle?: number): boolean {
    const hadShield = this.shield > 0;

    // Use base class takeDamage for actual damage calculation
    const result = this.takeDamage(amount);

    // Visual feedback - shield hit effect if shield absorbed damage
    if (hadShield && result.shieldDamage > 0) {
      this.spawnShieldHitEffect(hitAngle);
    }

    // Visual feedback - flash (only for hull damage, or if no shield)
    if (!hadShield || result.hullDamage > 0) {
      this.damageFlashTime = 100;
      this.shipGraphics.setAlpha(0.5);
    }

    // Emit damage event for UI
    emitGameEvent('playerDamage', {
      hull: this.hull,
      maxHull: this.maxHull,
      shield: this.shield,
      maxShield: this.maxShield,
      damage: amount,
    });

    // Auto-exit approach when attacked
    if (this._isAutopiloting && amount > 0) {
      this.stopAutopilot('combat');
    }

    return result.destroyed;
  }

  // Trigger hull damage red glow effect (for server-authoritative damage)
  public triggerDamageFlash(): void {
    this._hullGlowTime = 500; // 500ms red glow
    this.shipGraphics.setDamageGlow(true);
  }

  // Check if a point hits this entity (for projectile collision)
  public checkHit(hitX: number, hitY: number): boolean {
    if (this.isDestroyed) return false;
    const dx = hitX - this.x;
    const dy = hitY - this.y;
    return Math.sqrt(dx * dx + dy * dy) <= this.collisionRadius;
  }

  // Override onDestroy hook from DamageableEntity
  protected override onDestroy(): void {
    // Hide ship graphics
    this.shipGraphics.setVisible(false);

    // Stop strafe flares
    if (this.strafeLeftFlare) this.strafeLeftFlare.hide();
    if (this.strafeRightFlare) this.strafeRightFlare.hide();

    // Asteroids-style SVG explosion — real ship pieces drift apart
    if (this.parent) {
      createSVGExplosion(this.parent, this.x, this.y, this.rotation, this.shipClass.id, this._shipColor);
    }

    // Stop physics
    this.velocityX = 0;
    this.velocityY = 0;
    this.acceleration.x = 0;
    this.acceleration.y = 0;

    // Cancel any active approach (autopilot) — dead ships don't chase targets
    if (this._isAutopiloting) {
      this.stopAutopilot('combat');
    }

    // Emit death event
    emitGameEvent('playerDeath', undefined);

    // Clear scanned radar entities on death
    emitGameEvent('radarClearEntities', undefined);
  }

  // Explode the ship
  public explode(): void {
    if (this.isDestroyed) return;
    this.isDestroyed = true;
    this.onDestroy();
  }

  // Disable the ship (Red zone death - ship not destroyed, but disabled)
  public disable(): void {
    if (this.isDestroyed) return;
    this.isDestroyed = true;

    // Dim ship graphics to show disabled state (don't hide completely)
    this.shipGraphics.setAlpha(0.3);

    // Stop strafe flares
    if (this.strafeLeftFlare) this.strafeLeftFlare.hide();
    if (this.strafeRightFlare) this.strafeRightFlare.hide();

    // Create sparks/smoke effect instead of explosion debris
    this.createDisableEffect();

    // Stop physics
    this.velocityX = 0;
    this.velocityY = 0;
    this.acceleration.x = 0;
    this.acceleration.y = 0;
  }

  // Create visual effect for disabled ship
  private createDisableEffect(): void {
    // Create smoke particles
    const smokeColors = [0x444444, 0x666666, 0x888888];
    for (let i = 0; i < 10; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 20 + Math.random() * 30;
      const gfx = new Graphics();
      const color = smokeColors[Math.floor(Math.random() * smokeColors.length)];
      gfx.circle(0, 0, 3 + Math.random() * 4);
      gfx.fill({ color, alpha: 0.7 });
      gfx.position.set(this.x, this.y);

      // Add to parent
      if (this.parent) {
        this.parent.addChild(gfx);
      }

      // Animate smoke rising
      const targetX = this.x + Math.cos(angle) * speed;
      const targetY = this.y + Math.sin(angle) * speed - 30;
      tween({
        targets: gfx,
        x: targetX,
        y: targetY,
        alpha: 0,
        duration: 1500 + Math.random() * 500,
        onComplete: () => gfx.destroy(),
      });
    }

    // Create spark particles
    const sparkColors = [0xffff00, 0xffaa00, 0xff6600];
    for (let i = 0; i < 8; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 30 + Math.random() * 50;
      const gfx = new Graphics();
      const color = sparkColors[Math.floor(Math.random() * sparkColors.length)];
      gfx.circle(0, 0, 1 + Math.random() * 2);
      gfx.fill({ color, alpha: 1 });
      gfx.position.set(this.x, this.y);

      // Add to parent
      if (this.parent) {
        this.parent.addChild(gfx);
      }

      const targetX = this.x + Math.cos(angle) * speed;
      const targetY = this.y + Math.sin(angle) * speed;
      tween({
        targets: gfx,
        x: targetX,
        y: targetY,
        alpha: 0,
        duration: 500 + Math.random() * 300,
        onComplete: () => gfx.destroy(),
      });
    }
  }

  // Check if player is dead (inherited isDead() from DamageableEntity uses isDestroyed)
  // Override to use hull check for consistency with old behavior
  public override isDead(): boolean {
    return this.isDestroyed || this.hull <= 0;
  }

  /**
   * Override canAcceptInput to also block input while scanning.
   * Player cannot move while actively scanning (holding R).
   */
  override canAcceptInput(): boolean {
    // Block input if stunned (base behavior) or actively scanning
    return !this.isStunned && !this._isScanning;
  }

  // Respawn player
  public respawn(x: number, y: number): void {
    this.isDestroyed = false;
    this.hull = this.maxHull;
    this.shield = this.maxShield;
    this.x = x;
    this.y = y;
    this._angle = 0;
    this.rotation = 0;

    // Clear debris
    this.debris.forEach(d => d.destroy());
    this.debris = [];

    // Show ship graphics
    this.shipGraphics.setVisible(true);
    this.shipGraphics.setAlpha(1);

    // Reset physics
    this.velocityX = 0;
    this.velocityY = 0;

    // Emit respawn event
    emitGameEvent('playerRespawn', {
      hull: this.hull,
      maxHull: this.maxHull,
      shield: this.shield,
      maxShield: this.maxShield,
    });
  }

  // Equipment management
  public equipItem(slotIndex: number, itemId: string): boolean {
    if (slotIndex < 0 || slotIndex >= this.slots.length) return false;
    this.slots[slotIndex].equipped = itemId;
    this.recalculateStats();
    return true;
  }

  public unequipItem(slotIndex: number): string | undefined {
    if (slotIndex < 0 || slotIndex >= this.slots.length) return undefined;
    const itemId = this.slots[slotIndex].equipped;
    this.slots[slotIndex].equipped = undefined;
    this.recalculateStats();
    return itemId;
  }

  public getSlotByPosition(position: SlotDefinition['position']): SlotDefinition | undefined {
    return this.slots.find(slot => slot.position === position);
  }

  // Get current speed for UI
  public getSpeed(): number {
    return Math.sqrt(this.velocityX ** 2 + this.velocityY ** 2);
  }

  // Get position for UI
  public getCoordinates(): { x: number; y: number } {
    return {
      x: Math.floor(this.x),
      y: Math.floor(this.y),
    };
  }

  // Get rotation for UI (radar) - returns degrees, where 0 = up
  public getRotation(): number {
    return this._angle;
  }

  // Get ship name for UI
  public getShipName(): string {
    return this.shipClass.name;
  }

  // Get/set ship color
  public getShipColor(): number {
    return this._shipColor;
  }

  public setShipColor(color: number): void {
    this._shipColor = color;
    this.shipGraphics.shipColor = color;
  }

  // Get thrust state for network sync
  public getIsThrusting(): boolean {
    return this.isThrustingForward;
  }

  // Get strafe state for potential future network sync
  public getIsStrafing(): boolean {
    return this._isStrafing;
  }

  // Get boost state for network sync
  public getIsBoosting(): boolean {
    return this._isBoosting;
  }

  // Get boost energy (0-100)
  public getBoostEnergy(): number {
    return this.boostEnergy;
  }

  // Get boost max energy
  public getBoostMaxEnergy(): number {
    return this.boostMaxEnergy;
  }

  // Get boost energy as percentage (0-1)
  public getBoostEnergyPercent(): number {
    return this.boostEnergy / this.boostMaxEnergy;
  }

  // Check if player has a booster equipped
  public hasBooster(): boolean {
    return this.getEquippedBooster() !== null;
  }

  // ==================== UTILITY HOTBAR ====================

  /**
   * Check for hotbar key presses (1-5) and activate corresponding utility slot.
   */
  private handleHotbarInput(): void {
    for (let i = 0; i < this.hotbarKeys.length; i++) {
      const key = this.hotbarKeys[i];
      if (key && key.justDown) {
        this.activateUtilitySlot(i);
      }
    }
  }

  // Active utility types that can be triggered from hotbar (only true UTILITY items)
  private static readonly ACTIVE_UTILITY_TYPES = new Set([
    'beacon_deployer', 'cargo_scanner',
  ]);

  /**
   * Get all equipped utilities mapped to hotbar slots (max 5).
   * Active utilities are sorted first, then passive.
   */
  public getUtilityHotbarState(): Array<{
    slotIndex: number;
    equipped: boolean;
    utilityType: string;
    utilityName: string;
    utilityId: string;
    tier: number;
    isActive: boolean;
    isOnCooldown: boolean;
    cooldownPercent: number;
    cooldownRemaining: number;
    isPassive: boolean;
    energyPercent?: number;
  } | null> {
    const now = Date.now();
    const utilities: Array<{ utility: Utility; slotIdx: number }> = [];

    // Collect equipped utilities from UTILITY slots only (not ENHANCER)
    for (let i = 0; i < this.slots.length; i++) {
      const slot = this.slots[i];
      if (slot.type !== 'UTILITY' || !slot.equipped) continue;
      const equipment = getEquipmentById(slot.equipped);
      if (!equipment || equipment.category !== 'UTILITY') continue;
      utilities.push({ utility: equipment as Utility, slotIdx: i });
    }

    // Sort by tier desc
    utilities.sort((a, b) => b.utility.tier - a.utility.tier);

    // Map to hotbar slots (max 5)
    const result: Array<{
      slotIndex: number;
      equipped: boolean;
      utilityType: string;
      utilityName: string;
      utilityId: string;
      tier: number;
      isActive: boolean;
      isOnCooldown: boolean;
      cooldownPercent: number;
      cooldownRemaining: number;
      isPassive: boolean;
      energyPercent?: number;
    } | null> = [null, null, null, null, null];

    for (let i = 0; i < Math.min(utilities.length, 5); i++) {
      const { utility } = utilities[i];
      const isPassive = false; // All UTILITY slot items are active/accionable
      const cooldown = this.utilityCooldowns.get(utility.utilityType);
      const isOnCooldown = cooldown ? now < cooldown.until : false;
      const cooldownRemaining = isOnCooldown && cooldown ? cooldown.until - now : 0;
      const cooldownPercent = isOnCooldown && cooldown ? cooldownRemaining / cooldown.total : 0;

      let isActive = false;
      let energyPercent: number | undefined;

      // Active utilities are click-to-activate, not continuously active
      // (beacon_deployer, cargo_scanner - state tracked via cooldowns)

      result[i] = {
        slotIndex: i,
        equipped: true,
        utilityType: utility.utilityType,
        utilityName: utility.name,
        utilityId: utility.id,
        tier: utility.tier,
        isActive,
        isOnCooldown,
        cooldownPercent,
        cooldownRemaining,
        isPassive,
        energyPercent,
      };
    }

    return result;
  }

  /**
   * Activate a utility by hotbar slot index (0-4).
   * Called when player presses keys 1-5 or clicks the hotbar.
   */
  public activateUtilitySlot(slotIndex: number): void {
    const hotbarState = this.getUtilityHotbarState();
    const slot = hotbarState[slotIndex];
    if (!slot || !slot.equipped || slot.isPassive || slot.isOnCooldown) return;

    switch (slot.utilityType) {
      case 'booster':
        // Toggle boost - simulate key press/release
        // Boost is already handled by hold key; for hotbar click, toggle on/off
        // Not needed since boost uses hold-key. Hotbar just shows state.
        break;
      case 'beacon_deployer':
        // Deploy beacon at current position
        this.activateBeaconDeployer();
        break;
      case 'sensor_array':
        // Radar is hold-to-scan, hotbar just shows state
        break;
      case 'cargo_scanner':
        this.activateCargoScanner();
        break;
      case 'warp_disruptor':
        this.activateWarpDisruptor();
        break;
      case 'fighter_drone_bay':
      case 'miner_drone_bay':
        this.activateDroneBay(slot.utilityType);
        break;
    }
  }

  /**
   * Deploy an interdiction beacon at the player's current position.
   */
  private activateBeaconDeployer(): void {
    const now = Date.now();

    // Find equipped beacon deployer
    const beaconSlot = this.slots.find(slot => {
      if (slot.type !== 'UTILITY' || !slot.equipped) return false;
      const eq = getEquipmentById(slot.equipped);
      if (!eq || eq.category !== 'UTILITY') return false;
      return (eq as Utility).utilityType === 'beacon_deployer';
    });

    if (!beaconSlot || !beaconSlot.equipped) return;
    const beacon = getEquipmentById(beaconSlot.equipped) as Utility;
    if (!beacon) return;

    const cooldown = beacon.stats.beaconDeployCooldown || 30000;

    // Check local cooldown
    const cd = this.utilityCooldowns.get('beacon_deployer');
    if (cd && now < cd.until) return;

    // Set cooldown
    this.utilityCooldowns.set('beacon_deployer', { until: now + cooldown, total: cooldown });

    // Emit to game scene / network
    emitGameEvent('activateUtilitySlot', -1); // Signal beacon deploy via special value
  }

  /**
   * Activate warp disruptor field - blocks enemy warp in range for a duration.
   */
  private activateWarpDisruptor(): void {
    const now = Date.now();

    // Find equipped warp disruptor
    const disruptorSlot = this.slots.find(slot => {
      if (slot.type !== 'UTILITY' || !slot.equipped) return false;
      const eq = getEquipmentById(slot.equipped);
      if (!eq || eq.category !== 'UTILITY') return false;
      return (eq as Utility).utilityType === 'warp_disruptor';
    });

    if (!disruptorSlot || !disruptorSlot.equipped) return;
    const disruptor = getEquipmentById(disruptorSlot.equipped) as Utility;
    if (!disruptor) return;

    const duration = disruptor.stats.warpDisruptDuration || 3000;
    const cooldown = disruptor.stats.warpDisruptCooldown || 15000;

    // Check local cooldown
    const cd = this.utilityCooldowns.get('warp_disruptor');
    if (cd && now < cd.until) return;

    // Set cooldown (duration + cooldown combined)
    const totalCooldown = duration + cooldown;
    this.utilityCooldowns.set('warp_disruptor', { until: now + totalCooldown, total: totalCooldown });

    // Send to server
    emitGameEvent('activateUtilitySlot', -2); // Signal disruptor activate via special value
  }

  /**
   * Activate cargo scanner - scans selected target's cargo contents.
   * Requires a player target selected and in range.
   */
  private activateCargoScanner(): void {
    const now = Date.now();

    // Must have a player target selected
    const target = this.selectedTarget;
    if (!target || target.type !== 'player') {
      emitGameEvent('notification', { type: 'warning', message: 'Select a player to scan' });
      return;
    }

    // Find equipped cargo scanner
    const scannerSlot = this.slots.find(slot => {
      if (slot.type !== 'UTILITY' || !slot.equipped) return false;
      const eq = getEquipmentById(slot.equipped);
      if (!eq || eq.category !== 'UTILITY') return false;
      return (eq as Utility).utilityType === 'cargo_scanner';
    });

    if (!scannerSlot || !scannerSlot.equipped) return;
    const scanner = getEquipmentById(scannerSlot.equipped) as Utility;
    if (!scanner) return;

    const cooldown = scanner.stats.scanCooldown || 10000;
    const duration = scanner.stats.scanDuration || 2000;

    // Check local cooldown
    const cd = this.utilityCooldowns.get('cargo_scanner');
    if (cd && now < cd.until) return;

    // Set cooldown (duration + cooldown)
    const totalCooldown = duration + cooldown;
    this.utilityCooldowns.set('cargo_scanner', { until: now + totalCooldown, total: totalCooldown });

    // Send to server via special value -3
    emitGameEvent('activateUtilitySlot', -3);
  }

  /**
   * Toggle deploy/recall ship drones via the utility hotbar.
   */
  private activateDroneBay(utilityType: string): void {
    const now = Date.now();
    const DRONE_BAY_COOLDOWN = 5000; // 5 second cooldown between toggle actions

    // Check local cooldown (shared across all drone bay types)
    const cd = this.utilityCooldowns.get(utilityType);
    if (cd && now < cd.until) return;

    // Set cooldown
    this.utilityCooldowns.set(utilityType, { until: now + DRONE_BAY_COOLDOWN, total: DRONE_BAY_COOLDOWN });

    // Send to server
    emitGameEvent('activateUtilitySlot', -4);
  }

  // ==================== AUTOPILOT / APPROACH ====================

  /**
   * Check if autopilot is active.
   */
  public get isAutopiloting(): boolean {
    return this._isAutopiloting;
  }

  /**
   * Start autopilot toward target coordinates.
   * @param targetX - Destination X coordinate
   * @param targetY - Destination Y coordinate
   * @param onStop - Optional callback when autopilot stops
   */
  public startAutopilot(targetX: number, targetY: number, onStop?: () => void, entityRadius: number = 0): void {
    this._isAutopiloting = true;
    this.autopilotTarget = { x: targetX, y: targetY, entityRadius };
    this.autopilotStopCallback = onStop || null;

    // Emit event for UI
    emitGameEvent('fastTravelStarted', {
      targetX,
      targetY,
    });

    // (tutorial v2 removed the approach trigger — Continue is manual)
  }

  /**
   * Update autopilot destination in place (used by follow-mode to track moving targets).
   * No-op if autopilot is not engaged.
   */
  public updateAutopilotTarget(targetX: number, targetY: number, entityRadius?: number): void {
    if (!this._isAutopiloting || !this.autopilotTarget) return;
    this.autopilotTarget.x = targetX;
    this.autopilotTarget.y = targetY;
    if (entityRadius !== undefined) this.autopilotTarget.entityRadius = entityRadius;
  }

  /**
   * Stop autopilot and restore player control.
   * @param reason - Why autopilot stopped (arrival, combat, manual)
   */
  public stopAutopilot(reason: 'arrival' | 'combat' | 'manual' = 'manual'): void {
    if (!this._isAutopiloting) return;

    this._isAutopiloting = false;
    this.autopilotTarget = null;

    // Kill velocity on disengage so the ship actually stops
    if (reason === 'manual' || reason === 'arrival') {
      this.velocityX = 0;
      this.velocityY = 0;
      this.acceleration.x = 0;
      this.acceleration.y = 0;
      this.isBraking = false;
    }

    // Emit event for UI
    emitGameEvent('fastTravelStopped', { reason });

    // Call callback if provided
    if (this.autopilotStopCallback) {
      this.autopilotStopCallback();
      this.autopilotStopCallback = null;
    }
  }

  /**
   * Handle autopilot movement - steer toward target with collision avoidance.
   */
  private handleAutopilotMovement(delta: number): void {
    if (!this.autopilotTarget) {
      this.stopAutopilot('manual');
      return;
    }

    const stats = this.effectiveStats;
    const targetX = this.autopilotTarget.x;
    const targetY = this.autopilotTarget.y;

    // Calculate distance to target center
    const dx = targetX - this.x;
    const dy = targetY - this.y;
    const distanceToCenter = Math.sqrt(dx * dx + dy * dy);
    const speed = this.getSpeed();

    // Stop distance: entity radius + ship radius + safety buffer
    // This ensures the ship stops OUTSIDE the collision zone
    const shipRadius = COLLISION_CONFIG.SHIP_COLLISION_RADIUS;
    const stopDistance = this.autopilotTarget.entityRadius + shipRadius + 30; // 30 unit safety buffer

    // Effective distance = how far we are from our stop point (edge of entity, not center)
    const distance = Math.max(0, distanceToCenter - stopDistance);

    // Calculate required braking distance based on current speed
    // Using physics: d = v^2 / (2 * a) with safety margin
    const brakingDeceleration = stats.brakingPower * 0.8; // Use 80% of braking power for safety
    const requiredBrakingDistance = (speed * speed) / (2 * brakingDeceleration) + 50; // Add 50 unit buffer

    // Check if we're close enough and slow enough to consider "arrived"
    if (distance < 30 && speed < 10) {
      this.velocityX = 0;
      this.velocityY = 0;
      this.acceleration.x = 0;
      this.acceleration.y = 0;
      this.stopAutopilot('arrival');
      return;
    }

    // Start braking when within required braking distance
    if (distance < requiredBrakingDistance) {
      // Apply braking - turn around and brake or just decelerate
      this.applyAutopilotBraking(delta, dx, dy, distance);
      return;
    }

    // Calculate desired angle to target
    const targetAngle = Math.atan2(dy, dx);
    const targetAngleDeg = radToDeg(targetAngle) + 90; // Ship faces up at angle 0

    // Get obstacles and calculate avoidance steering
    const avoidanceAngle = this.calculateCollisionAvoidance(targetAngle);

    // Apply avoidance if needed
    let steerAngle = targetAngleDeg;
    if (avoidanceAngle !== 0) {
      steerAngle = targetAngleDeg + radToDeg(avoidanceAngle);
    }

    // Smooth rotation toward target angle
    let angleDiff = steerAngle - this._angle;
    while (angleDiff > 180) angleDiff -= 360;
    while (angleDiff < -180) angleDiff += 360;

    const rotationSpeed = stats.rotationSpeed * 0.05;
    if (Math.abs(angleDiff) < rotationSpeed) {
      this._angle = steerAngle;
    } else {
      this._angle += Math.sign(angleDiff) * rotationSpeed;
    }
    this.rotation = degToRad(this._angle);
    this.angularVelocity = 0;

    // Apply thrust if roughly facing target (within 45 degrees)
    const facingAngleDiff = Math.abs(angleDiff);
    if (facingAngleDiff < 45) {
      const thrustAngle = degToRad(this._angle - 90);
      const acceleration = stats.acceleration;

      this.acceleration.x = Math.cos(thrustAngle) * acceleration;
      this.acceleration.y = Math.sin(thrustAngle) * acceleration;
      this.isThrustingForward = true;
      this.shipGraphics.thrusting = true;

      // Smart boost during autopilot: use boost when available (above minimum)
      // and recharge when depleted - creates efficient boost cycling
      // Combat-overhaul: boost is native, no equipment gate
      if (this.boostEnergy >= this.boostMinToStart) {
        this._isBoosting = true;
      } else if (this.boostEnergy <= 0) {
        this._isBoosting = false;
      }
      // While boosting, energy is drained in handleMovement/boost logic
    } else {
      // Slow down while turning - don't boost
      this.acceleration.x = 0;
      this.acceleration.y = 0;
      this.isThrustingForward = false;
      this.shipGraphics.thrusting = false;
      this._isBoosting = false;
    }

    // Enforce max speed (boost multiplier is applied in handleMovement)
    this.currentMaxVelocity = stats.maxSpeed;
  }

  /**
   * Apply gradual braking for arrival.
   * Uses the space braking mechanic - applies reverse thrust to slow down.
   */
  private applyAutopilotBraking(_delta: number, dx: number, dy: number, distance: number): void {
    const stats = this.effectiveStats;
    const speed = this.getSpeed();

    // Stop boosting during braking
    this._isBoosting = false;

    if (speed > 5) {
      // Calculate the angle opposite to our velocity (to brake)
      const velAngle = Math.atan2(this.velocityY, this.velocityX);

      // Check for obstacles even during braking — if we're drifting toward something,
      // apply a lateral correction thrust instead of pure reverse braking
      const targetAngle = Math.atan2(dy, dx);
      const avoidanceAngle = this.calculateCollisionAvoidance(targetAngle);

      if (avoidanceAngle !== 0 && speed > 20) {
        // We're braking but drifting toward an obstacle — apply lateral dodge thrust
        // Mix braking (reverse velocity) with lateral avoidance
        const dodgeAngle = velAngle + Math.PI + avoidanceAngle; // Reverse + lateral offset
        const urgency = Math.max(0.5, 1 - (distance / 500));
        const brakeForce = stats.brakingPower * urgency;

        this.acceleration.x = Math.cos(dodgeAngle) * brakeForce;
        this.acceleration.y = Math.sin(dodgeAngle) * brakeForce;
      } else {
        // Normal braking — apply stronger braking force based on distance
        const urgency = Math.max(0.5, 1 - (distance / 500)); // More urgent when closer
        const brakeForce = stats.brakingPower * urgency;

        this.acceleration.x = -Math.cos(velAngle) * brakeForce;
        this.acceleration.y = -Math.sin(velAngle) * brakeForce;
      }

      // Visual: show brake sparks when braking hard
      this.isBraking = speed > 30;
    } else {
      // Nearly stopped - zero out and disengage autopilot
      this.velocityX = 0;
      this.velocityY = 0;
      this.acceleration.x = 0;
      this.acceleration.y = 0;
      this.isBraking = false;
      this.stopAutopilot('arrival');
      return;
    }

    // Turn ship to face the target while braking (looks better)
    const targetAngle = Math.atan2(dy, dx);
    const targetAngleDeg = radToDeg(targetAngle) + 90;
    let angleDiff = targetAngleDeg - this._angle;
    while (angleDiff > 180) angleDiff -= 360;
    while (angleDiff < -180) angleDiff += 360;

    const rotationSpeed = stats.rotationSpeed * 0.03; // Slower rotation during braking
    if (Math.abs(angleDiff) > rotationSpeed) {
      this._angle += Math.sign(angleDiff) * rotationSpeed;
      this.rotation = degToRad(this._angle);
    }

    this.isThrustingForward = false;
    this.shipGraphics.thrusting = false;
  }

  /**
   * Calculate steering angle to avoid obstacles ahead.
   * Uses summed repulsive forces from ALL nearby obstacles for smooth multi-obstacle avoidance.
   * Avoidance margin scales with speed so fast ships steer earlier.
   * @returns Avoidance angle offset in radians (0 = no avoidance)
   */
  private calculateCollisionAvoidance(_targetAngle: number): number {
    // Get obstacles from parent scene via custom data or event
    const obstacles = this.getData('obstaclesForAvoidance') as Array<{ x: number; y: number; radius: number }> | undefined;
    if (!obstacles || obstacles.length === 0) return 0;

    const speed = this.getSpeed();
    // Dynamic lookAhead based on speed - faster = look further ahead
    const baseLookAhead = 400;
    const lookAheadDistance = Math.max(baseLookAhead, speed * 2.5);
    const shipRadius = 30; // Approximate ship collision radius
    // Scale avoidance margin with speed: 80 at rest, up to 200 at high speed
    const avoidanceMargin = 80 + Math.min(120, speed * 0.3);

    const shipAngle = degToRad(this._angle - 90);

    // Accumulate repulsive force from all threatening obstacles
    let totalForceX = 0;
    let totalForceY = 0;

    for (const obstacle of obstacles) {
      const dx = obstacle.x - this.x;
      const dy = obstacle.y - this.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      const collisionRadius = obstacle.radius + shipRadius + avoidanceMargin;

      // Skip if too far to matter
      if (dist > lookAheadDistance + collisionRadius) continue;

      // Check if obstacle is roughly ahead (within 100 degrees of heading)
      const angleToObstacle = Math.atan2(dy, dx);
      let angleDiff = angleToObstacle - shipAngle;
      while (angleDiff > Math.PI) angleDiff -= Math.PI * 2;
      while (angleDiff < -Math.PI) angleDiff += Math.PI * 2;

      // Wider check angle (100°) so we notice obstacles we're about to side-swipe
      if (Math.abs(angleDiff) > Math.PI * 0.56) continue;

      // Project ship position forward and check distance to obstacle
      const projDist = dist * Math.cos(angleDiff);
      const lateralDist = dist * Math.sin(angleDiff); // Keep sign for direction

      if (projDist <= 0 || projDist > lookAheadDistance) continue;
      if (Math.abs(lateralDist) >= collisionRadius) continue;

      // This obstacle is on a collision course — compute repulsive force
      const clearanceNeeded = collisionRadius - Math.abs(lateralDist);
      const clearanceFactor = Math.min(1, clearanceNeeded / collisionRadius);

      // Urgency: exponential increase as we get closer
      const urgency = Math.pow(1 - (projDist / lookAheadDistance), 1.5);

      // Force magnitude combines urgency and how deep into the collision zone we are
      const forceMag = urgency * (0.5 + clearanceFactor * 0.5);

      // Repel perpendicular to heading, away from obstacle center
      // lateralDist > 0 means obstacle is to the left → push right (negative angle)
      const repelDirection = lateralDist > 0 ? -1 : 1;
      // If lateralDist is near zero (head-on), default to pushing right to break symmetry
      const effectiveDirection = Math.abs(lateralDist) < 5 ? -1 : repelDirection;

      // Decompose force into perpendicular-to-heading components
      const perpAngle = shipAngle + (effectiveDirection > 0 ? Math.PI / 2 : -Math.PI / 2);
      totalForceX += Math.cos(perpAngle) * forceMag;
      totalForceY += Math.sin(perpAngle) * forceMag;
    }

    // Convert accumulated force to a steering angle offset
    const totalForceMag = Math.sqrt(totalForceX * totalForceX + totalForceY * totalForceY);
    if (totalForceMag < 0.01) return 0;

    // Determine the signed angle offset from the accumulated force direction
    const forceAngle = Math.atan2(totalForceY, totalForceX);
    let steerOffset = forceAngle - shipAngle;
    while (steerOffset > Math.PI) steerOffset -= Math.PI * 2;
    while (steerOffset < -Math.PI) steerOffset += Math.PI * 2;

    // Cap the max avoidance angle — scale with force magnitude but cap at 75°
    const maxAvoidanceStrength = Math.PI * 0.42; // 75 degrees (up from 60 for multi-obstacle)
    const cappedMag = Math.min(totalForceMag, 1.5); // Clamp accumulated force
    const avoidanceStrength = maxAvoidanceStrength * (cappedMag / 1.5);

    // Apply the sign of the steer offset with the capped magnitude
    return Math.sign(steerOffset) * avoidanceStrength;
  }

  // ==================== WARP TRAVEL ====================

  /**
   * Get current warp phase.
   */
  public get warpPhase(): WarpPhase {
    return this._warpPhase;
  }

  /**
   * Get warp target coordinates (for UI and sending to server).
   * During 'ready' phase, uses autopilot target.
   * During other phases, uses server-provided coordinates.
   */
  public getWarpTarget(): { x: number; y: number } | null {
    if (this._warpPhase === 'none') return null;

    // During 'ready' phase, use waypoint (server hasn't confirmed yet)
    if (this._warpPhase === 'ready') {
      return { x: this._lastWaypointX, y: this._lastWaypointY };
    }

    return { x: this._warpTargetX, y: this._warpTargetY };
  }

  /**
   * Get warp cooldown end time (for UI).
   */
  public getWarpCooldownUntil(): number {
    return this._warpCooldownUntil;
  }

  /**
   * Check if warp is on cooldown.
   */
  public isWarpOnCooldown(): boolean {
    return Date.now() < this._warpCooldownUntil;
  }

  /**
   * Update warp waypoint for readiness detection.
   * Called from GameScene when waypoint is set or changed.
   * Warp preparation starts immediately.
   */
  public setWarpWaypoint(x: number, y: number): void {
    const changed = this._lastWaypointX !== x || this._lastWaypointY !== y;
    this._lastWaypointX = x;
    this._lastWaypointY = y;
    if (changed) {
      // Reset preparation on waypoint change
      this._warpReadyStartTime = 0;
      if (this._warpPhase === 'ready') {
        this._warpPhase = 'none';
        emitGameEvent('warpReadyChanged', { ready: false });
      }
    }
  }

  /**
   * Clear warp waypoint. Cancels any warp preparation in progress.
   */
  public clearWarpWaypoint(): void {
    this._lastWaypointX = 0;
    this._lastWaypointY = 0;
    this._warpReadyStartTime = 0;
    if (this._warpPhase === 'ready') {
      this._warpPhase = 'none';
      emitGameEvent('warpReadyChanged', { ready: false });
    }
    this.resetWarpWarmingUp();
  }

  /**
   * Reset warp warming up state and emit event if was warming up.
   */
  private resetWarpWarmingUp(): void {
    if (this._warpReadyStartTime !== 0) {
      this._warpReadyStartTime = 0;
      emitGameEvent('warpWarmingUp', { progress: -1, remainingMs: -1 });  // -1 indicates stopped
    }
  }

  /**
   * Check if player is ready for warp.
   * Prepares as soon as a waypoint is set (no speed/alignment required).
   * Called every frame when a waypoint exists and warp phase is 'none'.
   */
  private checkWarpReadiness(): void {
    // Don't check if on cooldown
    if (this.isWarpOnCooldown()) {
      this.resetWarpWarmingUp();
      return;
    }

    // Need a waypoint
    if (this._lastWaypointX === 0 && this._lastWaypointY === 0) {
      this.resetWarpWarmingUp();
      return;
    }

    // Check distance (don't activate warp too close to destination)
    const dx = this._lastWaypointX - this.x;
    const dy = this._lastWaypointY - this.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    if (distance < 500) {  // Too close, no point in warp
      this.resetWarpWarmingUp();
      return;
    }

    // Start or continue tracking preparation time
    const now = Date.now();
    if (this._warpReadyStartTime === 0) {
      this._warpReadyStartTime = now;
      // Emit initial warming up event
      emitGameEvent('warpWarmingUp', {
        progress: 0,
        remainingMs: WARP_CONFIG.SUSTAINED_MOVEMENT_TIME_MS,
      });
      return;
    }

    // Check if preparation complete
    const elapsedMs = now - this._warpReadyStartTime;
    if (elapsedMs >= WARP_CONFIG.SUSTAINED_MOVEMENT_TIME_MS) {
      // Emit warp ready event (only once)
      if (this._warpPhase === 'none') {
        this._warpPhase = 'ready';
        emitGameEvent('warpReadyChanged', {
          ready: true,
          targetX: this._lastWaypointX,
          targetY: this._lastWaypointY,
        });
      }
    } else {
      // Emit warming up progress
      const progress = elapsedMs / WARP_CONFIG.SUSTAINED_MOVEMENT_TIME_MS;
      const remainingMs = WARP_CONFIG.SUSTAINED_MOVEMENT_TIME_MS - elapsedMs;
      emitGameEvent('warpWarmingUp', { progress, remainingMs });
    }
  }

  /**
   * Set warp state from server.
   * Called by GameScene when server sends warpStateChanged.
   */
  public setWarpState(
    phase: WarpPhase,
    targetX?: number,
    targetY?: number,
    warpSpeed?: number,
    cooldownUntil?: number
  ): void {
    const previousPhase = this._warpPhase;
    this._warpPhase = phase;

    if (targetX !== undefined) this._warpTargetX = targetX;
    if (targetY !== undefined) this._warpTargetY = targetY;
    if (warpSpeed !== undefined) this._warpSpeed = warpSpeed;
    if (cooldownUntil !== undefined) this._warpCooldownUntil = cooldownUntil;

    // Reset readiness tracking when entering calculating or higher
    if (phase === 'calculating' || phase === 'traveling') {
      this._warpReadyStartTime = 0;
    }

    // When warp starts, stop autopilot (server controls position now)
    if (phase === 'traveling' && previousPhase !== 'traveling') {
      this.stopAutopilot('manual');
      // Face the target
      const dx = this._warpTargetX - this.x;
      const dy = this._warpTargetY - this.y;
      this._angle = radToDeg(Math.atan2(dy, dx)) + 90;
      this.rotation = degToRad(this._angle);
      // Snap camera during warp — lerp can't keep up with warp speed
      this.camera?.setLerp(1.0);
    }

    // When warp ends, reset state
    if (phase === 'none' || phase === 'cooldown') {
      this._warpReadyStartTime = 0;
      // Restore smooth camera follow
      this.camera?.setLerp(0.1);
      // Clear warp trail
      if (this._warpTrailGraphics) {
        this._warpTrailGraphics.clear();
      }
    }

    // Emit state change event for UI
    emitGameEvent('warpStateChanged', {
      phase,
      targetX: this._warpTargetX,
      targetY: this._warpTargetY,
      cooldownUntil: this._warpCooldownUntil,
    });
  }

  /**
   * Cancel warp readiness (e.g., when server rejects or manual cancel).
   */
  public cancelWarpReadiness(): void {
    if (this._warpPhase === 'ready') {
      this._warpPhase = 'none';
      this._warpReadyStartTime = 0;
      emitGameEvent('warpReadyChanged', { ready: false });
    }
  }

  /**
   * Handle warp traveling movement.
   * Ship moves in straight line at warp speed, controlled by server state.
   */
  private handleWarpTraveling(delta: number): void {
    // Calculate direction to target
    const dx = this._warpTargetX - this.x;
    const dy = this._warpTargetY - this.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // Calculate ETA
    const eta = this._warpSpeed > 0 ? distance / this._warpSpeed : 0;

    // Emit warp state update for UI (distance and ETA)
    emitGameEvent('warpStateChanged', {
      phase: 'traveling',
      targetX: this._warpTargetX,
      targetY: this._warpTargetY,
      distance,
      estimatedTimeSeconds: eta,
    });

    if (distance < WARP_CONFIG.ARRIVAL_DISTANCE) {
      // Arrived at destination - stop the ship and exit warp
      this.x = this._warpTargetX;
      this.y = this._warpTargetY;
      this.velocityX = 0;
      this.velocityY = 0;

      // Exit warp (server will also handle this, but do it client-side for responsiveness)
      this._warpPhase = 'cooldown';
      this._warpCooldownUntil = Date.now() + WARP_CONFIG.COOLDOWN_MS;

      // Emit cooldown state
      emitGameEvent('warpStateChanged', {
        phase: 'cooldown',
        cooldownUntil: this._warpCooldownUntil,
      });

      // Clear warp trail
      if (this._warpTrailGraphics) {
        this._warpTrailGraphics.clear();
      }

      // Stop thrust visuals
      this.isThrustingForward = false;
      this.shipGraphics.thrusting = false;
      return;
    }

    // Normalize direction
    const nx = dx / distance;
    const ny = dy / distance;

    // Move at warp speed
    const dt = delta / 1000;
    const moveDistance = this._warpSpeed * dt;

    // Update position
    this.x += nx * moveDistance;
    this.y += ny * moveDistance;

    // Set velocity for visual purposes (but position is authoritative)
    this.velocityX = nx * this._warpSpeed;
    this.velocityY = ny * this._warpSpeed;

    // Update rotation to face target
    this._angle = radToDeg(Math.atan2(dy, dx)) + 90;
    this.rotation = degToRad(this._angle);

    // Show thrust effect during warp
    this.isThrustingForward = true;
    this.shipGraphics.thrusting = true;
  }

  // Warp calculation absorption particles (converge toward ship during 'calculating')
  private _calcAbsorptionParticles: Array<{
    angle: number;      // radial angle around ship
    dist: number;       // current distance from ship center
    startDist: number;  // initial spawn distance
    speed: number;      // inward speed (units per frame tick)
    size: number;       // particle radius
    color: number;      // from palette
    trail: number;      // trail length factor
  }> = [];
  private _calcAbsorptionTime: number = 0;

  /**
   * Update warp calculation absorption effect — particles converge from a radius toward the ship.
   * Active only during the 'calculating' phase.
   */
  private updateWarpCalculationEffect(): void {
    if (!this._warpTrailGraphics) return;

    if (this._warpPhase !== 'calculating') {
      // Clean up when leaving calculating phase
      if (this._calcAbsorptionParticles.length > 0) {
        this._calcAbsorptionParticles = [];
        this._calcAbsorptionTime = 0;
      }
      return;
    }

    const g = this._warpTrailGraphics;
    this._calcAbsorptionTime += 0.04;
    const t = this._calcAbsorptionTime;

    const calcPalette = [
      0x00aaff, // blue
      0x00ccff, // cyan
      0x20a5a6, // teal (matches PALETTE.TEAL)
      0x8855ff, // violet
      0xaa44ff, // purple
      0x00ddff, // bright cyan
      0x4488ff, // light blue
      0xffffff, // white sparks
    ];
    const pickColor = () => calcPalette[Math.floor(Math.random() * calcPalette.length)];

    const maxRadius = 180;  // spawn radius
    const maxParticles = 50;

    // Spawn new particles to maintain count
    while (this._calcAbsorptionParticles.length < maxParticles) {
      const angle = Math.random() * Math.PI * 2;
      const startDist = maxRadius * (0.6 + Math.random() * 0.4);
      this._calcAbsorptionParticles.push({
        angle,
        dist: startDist,
        startDist,
        speed: 0.8 + Math.random() * 1.5,
        size: 0.8 + Math.random() * 2.0,
        color: pickColor(),
        trail: 0.3 + Math.random() * 0.7,
      });
    }

    // Update and render particles
    for (let i = this._calcAbsorptionParticles.length - 1; i >= 0; i--) {
      const p = this._calcAbsorptionParticles[i];

      // Move inward
      p.dist -= p.speed;

      // Slight spiral rotation
      p.angle += 0.008 + (1 - p.dist / p.startDist) * 0.02;

      // Accelerate as particles get closer (gravitational pull feel)
      if (p.dist < maxRadius * 0.4) {
        p.speed *= 1.02;
      }

      // Recycle when absorbed
      if (p.dist <= 4) {
        const angle = Math.random() * Math.PI * 2;
        const startDist = maxRadius * (0.6 + Math.random() * 0.4);
        p.angle = angle;
        p.dist = startDist;
        p.startDist = startDist;
        p.speed = 0.8 + Math.random() * 1.5;
        p.size = 0.8 + Math.random() * 2.0;
        p.color = pickColor();
        p.trail = 0.3 + Math.random() * 0.7;
        continue;
      }

      const px = this.x + Math.cos(p.angle) * p.dist;
      const py = this.y + Math.sin(p.angle) * p.dist;

      // Alpha: fades in from edge, brightest near ship
      const normalizedDist = p.dist / maxRadius;
      const alpha = Math.min(0.9, 0.15 + (1 - normalizedDist) * 0.75);

      // Draw trail (line from current position outward along approach path)
      const trailLen = p.trail * 12 * (1 - normalizedDist + 0.3);
      const trailX = px + Math.cos(p.angle) * trailLen;
      const trailY = py + Math.sin(p.angle) * trailLen;
      g.moveTo(trailX, trailY);
      g.lineTo(px, py);
      g.stroke({ width: p.size * 0.6, color: p.color, alpha: alpha * 0.4 });

      // Draw particle dot
      const pSize = p.size + Math.sin(t * 8 + p.angle * 3) * 0.4;
      g.circle(px, py, pSize);
      g.fill({ color: p.color, alpha });

      // Bright core for larger particles
      if (p.size > 1.5) {
        g.circle(px, py, pSize * 0.5);
        g.fill({ color: 0xffffff, alpha: alpha * 0.8 });
      }
    }

    // Central absorbing glow (pulsing)
    const glowR = 8 + Math.sin(t * 6) * 3;
    g.circle(this.x, this.y, glowR + 15);
    g.fill({ color: 0x00aaff, alpha: 0.03 + Math.sin(t * 4) * 0.015 });
    g.circle(this.x, this.y, glowR + 8);
    g.fill({ color: 0x20a5a6, alpha: 0.06 + Math.sin(t * 5) * 0.02 });
    g.circle(this.x, this.y, glowR + 3);
    g.fill({ color: 0x00ccff, alpha: 0.15 + Math.sin(t * 7) * 0.05 });
    g.circle(this.x, this.y, glowR);
    g.fill({ color: 0xffffff, alpha: 0.4 + Math.sin(t * 8) * 0.1 });
  }

  // Warp trail animation state
  private _warpTrailTime: number = 0;
  // Parallel speed-lines that stream past the ship (all travel direction)
  private _warpLines: Array<{
    pos: number;       // position along travel axis (0 = far ahead, 1 = far behind)
    lateral: number;   // perpendicular offset from center (-1..1)
    speed: number;     // how fast it flows backward
    len: number;       // drawn length of the streak
    width: number;
    color: number;     // streak color from palette
  }> = [];
  // Small glowing particles that drift alongside the ship
  private _warpParticles: Array<{
    pos: number;       // along travel axis (0=ahead, 1=behind)
    lateral: number;
    speed: number;
    size: number;
    bright: boolean;   // true = white core, false = colored
    color: number;     // particle color from palette
  }> = [];

  /**
   * Update warp trail visual effect — parallel speed-lines and particles
   * streaming past the ship to simulate hyperspace travel.
   */
  private updateWarpTrailEffect(): void {
    if (!this._warpTrailGraphics) return;

    // Always clear the graphics first — each effect redraws fully per frame
    this._warpTrailGraphics.clear();

    // Run calculation absorption effect (draws on same graphics)
    this.updateWarpCalculationEffect();

    if (this._warpPhase !== 'traveling') {
      this._warpLines = [];
      this._warpParticles = [];
      this._warpTrailTime = 0;
      return;
    }
    this._warpTrailTime += 0.06;

    const speed = Math.sqrt(this.velocityX * this.velocityX + this.velocityY * this.velocityY);
    if (speed < 100) return;

    const g = this._warpTrailGraphics;
    const velAngle = Math.atan2(this.velocityY, this.velocityX);
    const cosV = Math.cos(velAngle);
    const sinV = Math.sin(velAngle);
    const cosP = Math.cos(velAngle + Math.PI / 2);
    const sinP = Math.sin(velAngle + Math.PI / 2);
    const warpColor = WARP_CONFIG.WARP_COLOR;
    const t = this._warpTrailTime;
    // Multi-color palette: blue, violet, red, orange, cyan
    const warpPalette = [
      0x00aaff, // blue (original)
      0x00ccff, // cyan
      0x8855ff, // violet
      0xaa44ff, // purple
      0xff3366, // red-pink
      0xff5533, // red-orange
      0xff8800, // orange
      0xffaa22, // amber
    ];
    const pickColor = () => warpPalette[Math.floor(Math.random() * warpPalette.length)];

    // Total visible range: ahead + behind
    const ahead = Math.min(speed * 0.5, 400);
    const behind = Math.min(speed * 1.0, 800);
    const totalRange = ahead + behind;
    // spreadRadius: how far laterally lines appear from center
    const spreadRadius = 80;

    // ─── 1. CENTRAL CORE TRAIL — soft glow along the center axis ───
    // Layered strokes behind the ship (fades to tail)
    const coreSegs = 12;
    for (let layer = 0; layer < 5; layer++) {
      const frac = layer / 4;
      const w = 40 * (1 - frac * 0.92);   // 40 → 3.2
      const a = 0.025 + frac * 0.12;      // 0.025 → 0.145
      const col = frac > 0.8 ? 0xffffff : warpColor;
      for (let s = 0; s < coreSegs; s++) {
        const r0 = s / coreSegs;
        const r1 = (s + 1) / coreSegs;
        const fadeA = a * (1 - r0 * 0.75);
        g.moveTo(this.x - cosV * behind * r0, this.y - sinV * behind * r0);
        g.lineTo(this.x - cosV * behind * r1, this.y - sinV * behind * r1);
        g.stroke({ width: w, color: col, alpha: fadeA });
      }
    }

    // ─── 2. PARALLEL SPEED-LINES — all perfectly parallel to travel direction ───
    const maxLines = 60;
    while (this._warpLines.length < maxLines) {
      const lat = (Math.random() - 0.5) * 2;
      // Avoid lines too close to center (that's where the core trail is)
      const absLat = Math.abs(lat);
      this._warpLines.push({
        pos: Math.random(),
        lateral: absLat < 0.15 ? (lat < 0 ? -0.15 - Math.random() * 0.85 : 0.15 + Math.random() * 0.85) : lat,
        speed: 1.0 + Math.random() * 1.8,
        len: 40 + Math.random() * 100,
        width: 0.5 + Math.random() * 2.0,
        color: pickColor(),
      });
    }

    for (const line of this._warpLines) {
      line.pos += line.speed * 0.018;

      // Recycle when past the tail
      if (line.pos > 1.15) {
        line.pos = -0.15 - Math.random() * 0.1;
        line.lateral = (Math.random() - 0.5) * 2;
        const absLat = Math.abs(line.lateral);
        if (absLat < 0.15) line.lateral = line.lateral < 0 ? -0.2 : 0.2;
        line.speed = 1.0 + Math.random() * 1.8;
        line.len = 40 + Math.random() * 100;
        line.width = 0.5 + Math.random() * 2.0;
        line.color = pickColor();
      }

      if (line.pos < 0 || line.pos > 1) continue;

      // Position: 0 = far ahead, 1 = far behind
      // Map to world: ahead at pos=0, ship at pos=(ahead/totalRange), behind at pos=1
      const axisOffset = -ahead + line.pos * totalRange; // negative=ahead, positive=behind
      const lat = line.lateral * spreadRadius;

      // Line start & end — both parallel to travel direction
      const halfLen = line.len / 2;
      const cx = this.x - cosV * axisOffset + cosP * lat;
      const cy = this.y - sinV * axisOffset + sinP * lat;
      const sx = cx + cosV * halfLen;
      const sy = cy + sinV * halfLen;
      const ex = cx - cosV * halfLen;
      const ey = cy - sinV * halfLen;

      // Alpha: brightest near ship (pos ≈ ahead/totalRange), fades at edges
      const shipPos = ahead / totalRange;
      const distFromShip = Math.abs(line.pos - shipPos);
      const lineAlpha = Math.max(0.05, 0.55 - distFromShip * 0.9);
      // Lines close to center are brighter/whiter, outer lines use their palette color
      const absLat = Math.abs(line.lateral);
      const lineColor = absLat < 0.2 ? 0xffffff : line.color;
      const finalAlpha = lineAlpha * (absLat < 0.2 ? 1.2 : 1.0);

      g.moveTo(sx, sy);
      g.lineTo(ex, ey);
      g.stroke({ width: line.width, color: lineColor, alpha: Math.min(finalAlpha, 0.6) });
    }

    // ─── 3. FLOATING PARTICLES — small dots drifting alongside ───
    const maxParticles = 35;
    while (this._warpParticles.length < maxParticles) {
      this._warpParticles.push({
        pos: Math.random(),
        lateral: (Math.random() - 0.5) * 2,
        speed: 0.6 + Math.random() * 1.4,
        size: 1 + Math.random() * 2.5,
        bright: Math.random() > 0.7,
        color: pickColor(),
      });
    }

    for (const p of this._warpParticles) {
      p.speed += (Math.random() - 0.5) * 0.02; // tiny drift
      p.pos += p.speed * 0.015;

      if (p.pos > 1.1) {
        p.pos = -0.1;
        p.lateral = (Math.random() - 0.5) * 2;
        p.speed = 0.6 + Math.random() * 1.4;
        p.size = 1 + Math.random() * 2.5;
        p.bright = Math.random() > 0.7;
        p.color = pickColor();
      }

      if (p.pos < 0 || p.pos > 1) continue;

      const axisOffset = -ahead + p.pos * totalRange;
      const lat = p.lateral * spreadRadius * 1.2; // particles spread wider
      const px = this.x - cosV * axisOffset + cosP * lat;
      const py = this.y - sinV * axisOffset + sinP * lat;

      const shipPos = ahead / totalRange;
      const dist = Math.abs(p.pos - shipPos);
      const pAlpha = Math.max(0.08, 0.5 - dist * 0.8);
      // Pulsing size
      const pSize = p.size + Math.sin(t * 6 + p.lateral * 5) * 0.6;

      if (p.bright) {
        // White core with colored halo
        g.circle(px, py, pSize + 2);
        g.fill({ color: p.color, alpha: pAlpha * 0.3 });
        g.circle(px, py, pSize);
        g.fill({ color: 0xffffff, alpha: pAlpha });
      } else {
        g.circle(px, py, pSize);
        g.fill({ color: p.color, alpha: pAlpha * 0.8 });
      }
    }

    // ─── 4. SHIP CORE GLOW — bright pulse at the ship ───
    const coreR = 5 + Math.sin(t * 10) * 2;
    g.circle(this.x, this.y, coreR + 12);
    g.fill({ color: warpColor, alpha: 0.05 });
    g.circle(this.x, this.y, coreR + 6);
    g.fill({ color: warpColor, alpha: 0.12 });
    g.circle(this.x, this.y, coreR + 2);
    g.fill({ color: warpColor, alpha: 0.3 });
    g.circle(this.x, this.y, coreR);
    g.fill({ color: 0xffffff, alpha: 0.75 });
  }

  // TargetableEntity abstract method implementation
  public getSelectionInfo(): SelectionInfo {
    return {
      type: this.entityType,
      id: this._playerId,
      name: this.shipClass.name,
      x: this.x,
      y: this.y,
      data: {
        shipClass: this.shipClass.id,
        hull: this.hull,
        maxHull: this.maxHull,
        shield: this.shield,
        maxShield: this.maxShield,
        credits: this.credits,
      },
    };
  }

  // Custom data storage
  public setData(key: string, value: unknown): this {
    this.customData.set(key, value);
    // Also update playerId if that's what's being set
    if (key === 'playerId' && typeof value === 'string') {
      this._playerId = value;
    }
    return this;
  }

  public getData(key: string): unknown {
    return this.customData.get(key);
  }

  // Cleanup
  override destroy(): void {
    // Unsubscribe from all global events
    this.eventUnsubscribers.forEach(unsub => unsub());
    this.eventUnsubscribers = [];
    this.strafeLeftFlare?.destroy();
    this.strafeRightFlare?.destroy();
    this.reverseLeftFlare?.destroy();
    this.reverseRightFlare?.destroy();
    this.brakeGraphics?.destroy();
    this.shieldGraphics?.destroy();
    this.visibilityGraphics?.destroy();
    this._warpTrailGraphics?.destroy();
    this._calcAbsorptionParticles = [];
    this._radarWaveGraphics?.destroy();
    this.brakeLineData = [];
    this.shieldArcData = [];
    this.radarWaves = [];
    this.debris.forEach(d => d.destroy());
    tweens.killTweensOf(this);
    super.destroy();
  }
}
